import React, { useState, useEffect, useRef, useMemo } from 'react';
import useTeamStore from '../../state/teamStore'; // FIXED import path
import { GoogleMap, Marker, Circle, InfoWindow } from '@react-google-maps/api';
import axios from 'axios';
import MyCustomerTeamList from '../customers/myCustomerTeamList';
import LocalUsersTeamList from '../customers/localUsersTeamList';
import LocalTeamsList from '../teams/LocalTeamsList';
import { useProfileStore } from '../../state/profileStore';
import useOtherUsersStore from '../../state/otherUsersStore';
import { useModalStore } from '../../state/modalStore';
import { useRecruitingStore } from '../../state/recruitingStore';
import RegisterTeamModal from '../teams/RegisterTeamModal';
import FocusAndLogisticsModal from '../common/FocusAndLogisticsModal';



function TeamInfo() {
  const { openRecruiting } = useModalStore();
  const { setTeamId, loadTeamList } = useRecruitingStore();
  // Zustand store FIRST (so selectors are safe to call below)
  const { profile, getServiceDistance, getCustomerFocus, refreshProfile, updateProfile } = useProfileStore();
  const { openTeam } = useModalStore();

  const [registerOpen, setRegisterOpen] = useState(false);
  const [inviteTeamPickOpen, setInviteTeamPickOpen] = useState(false);
  const [inviteTeamPickSelected, setInviteTeamPickSelected] = useState(null);
  const [isGlobalSearch, setIsGlobalSearch] = useState(false);
  const [globalUsers, setGlobalUsers] = useState([]);
  const [globalLoading, setGlobalLoading] = useState(false);


  // Use the store as the single source for profile in this file
  const profileUser = profile;

  // ── Lean-onboarding Slice 4: focus & logistics gate ─────────────────────
  // Post-lean-signup users have NULL u_customer_focus / b_customer_focus /
  // b_service_distance until they fill in the 3-question modal. The Teams
  // matching algorithm CAN'T recommend partners without all three signals
  // (you team up with businesses serving the same customer type within
  // overlapping geography), so we gate the entire page until they're set.
  // The modal renders as a backdrop overlay; the teams UI behind it is
  // grayed out but still mounted so we don't lose state when the modal
  // dismisses on save. Once filled, this re-renders with needsFocusGate=
  // false and the page unblocks immediately.
  const needsFocusGate = (
    !profile?.u_customer_focus ||
    (profile?.b_customer_focus == null && profile?.business?.b_customer_focus == null) ||
    (profile?.b_service_distance == null && profile?.business?.b_service_distance == null)
  );

  const { myTeams, activeTeamId, setMyTeams, setActiveTeamId, hydrateTeams } = useTeamStore();

    // Ensure the Team store is hydrated and the localStorage key (eh_team_v1) exists.
  useEffect(() => {
    // 1) Rehydrate from localStorage if it exists
    if (useTeamStore?.persist?.rehydrate) {
      useTeamStore.persist.rehydrate();
    }

    // 2) If the key is still missing, force a benign write so persist creates it.
    //    We use an existing action that updates a persisted field (updatedAtLocal).
    try {
      const hasKey = !!localStorage.getItem('eh_team_v1');
      if (!hasKey) {
        const snap = useTeamStore.getState();
        // This setter updates nearbyTeamsLocal + updatedAtLocal (both are persisted)
        snap.setNearbyTeamsLocal(Array.isArray(snap.nearbyTeamsLocal) ? snap.nearbyTeamsLocal : [], {
          center: snap.lastLocalCenter,
          radius: snap.lastLocalRadius,
        });
      }
    } catch (e) {
      console.warn('[TeamInfo] Unable to prime eh_team_v1:', e);
    }
  }, []);

  // Join Teams: allow map pins (and any other UI) to open TeamModal via event dispatch.
  useEffect(() => {
    const onOpenTeamModal = (e) => {
      const teamId = Number(
        e?.detail?.team_id ??
        e?.detail?.teamId ??
        e?.detail?.id
      );

      if (!Number.isFinite(teamId)) return;

      openTeam(teamId);
    };

    window.addEventListener('team:openModal', onOpenTeamModal);
    return () => window.removeEventListener('team:openModal', onOpenTeamModal);
  }, [openTeam]);

  // Help Center integration: switch radio or open create-team modal remotely
  useEffect(() => {
    const onSwitchLayer = (e) => {
      const layer = e?.detail?.layer;
      if (layer === 'local' || layer === 'candidates') setActiveLayer(layer);
    };
    const onOpenRegister = () => setRegisterOpen(true);

    window.addEventListener('team:switchLayer', onSwitchLayer);
    window.addEventListener('team:openRegister', onOpenRegister);
    return () => {
      window.removeEventListener('team:switchLayer', onSwitchLayer);
      window.removeEventListener('team:openRegister', onOpenRegister);
    };
  }, []);

  const displayUserFocus = getCustomerFocus() || 'B2BC';
  const [userTooltip, setUserTooltip] = useState('Loading...');
  const [, setRatingData] = useState({ u_avg_rating: null, u_review_count: 0 });
  const [, setTotalCustomers] = useState(0);
  const [serviceDistance, setServiceDistance] = useState(getServiceDistance());


  // tooltip for green pins
  const [candidateTip, setCandidateTip] = useState(null); // { pos: {lat,lng}, user }
  const candidateCloseTimer = useRef(null);
  const overCandidateInfo = useRef(false);
  // tooltip for BLUE team pins (Evaluate Teams)
  const [teamTip, setTeamTip] = useState(null);           // { pos: {lat,lng}, team }
  const teamCloseTimer = useRef(null);
  const overTeamInfo = useRef(false);



  // PINS
  const BLUE_PIN = 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png';
  const YELLOW_PIN = '/images/pins/gold-dot.png';




  const GREEN_PIN = `https://maps.google.com/mapfiles/ms/icons/green-dot.png`;
  // === Recolor the stock pin to exact brand gold (#facc15) ===




// Recolor only the GREEN BODY pixels (HSV), preserving black outline + black center dot exactly











  // --- Overlay state ---
  // Map<userId, { center: {lat,lng}, distanceMi: number, customers: Array<{lat,lng,id}> }>
  const [overlays, setOverlays] = useState(new Map());

  useEffect(() => {
  const fetchTooltips = async () => {
    try {
      const userFocus = profileUser?.u_customer_focus;
      
      // Fetch user tooltip
      const userResponse = await axios.get(
        `/api/tooltips/associate/${userFocus}?warning=false`,
        { withCredentials: true }
      );
      
      // Personalize the user tooltip
      setUserTooltip(
        `${profileUser?.u_first_name} represents ${profileUser?.b_name}, and ${userResponse.data?.t_template || ''}`
      );

    } catch (error) {
      console.error('Tooltip fetch error:', error);
      setUserTooltip('Error loading tooltip');
    }
  };

  if (profileUser) {
    fetchTooltips();
  }
}, [profileUser]);

  const [nearbyUsers, setNearbyUsers] = useState([]);
  const [nearbyLoading, setNearbyLoading] = useState(false);
  // Teams (Evaluate Teams: active teams near the viewer)
  const [nearbyTeams, setNearbyTeams] = useState([]);
  const [, setNearbyTeamsLoading] = useState(false);
  // Map of team_id -> { lat, lng, _src }
  const [teamGeoOverride, setTeamGeoOverride] = useState(new Map());
  // --- Micro-offset for stacked BLUE team pins (same lat/lng) ---
  const teamPinPosById = useMemo(() => {
    const out = new Map();

    // 1) Build base positions (same logic as the Marker block)
    const base = (Array.isArray(nearbyTeams) ? nearbyTeams : [])
      .map((t) => {
        const id = Number(t?.team_id ?? t?.id);
        if (!Number.isFinite(id)) return null;

        const ovr = teamGeoOverride.get(id);

        const pos = ovr
          ? { lat: ovr.lat, lng: ovr.lng }
          : (t?.b_lat != null && t?.b_lng != null) ? { lat: +t.b_lat, lng: +t.b_lng }
          : (t?.lat != null && t?.lng != null) ? { lat: +t.lat, lng: +t.lng }
          : null;

        if (!pos || !Number.isFinite(pos.lat) || !Number.isFinite(pos.lng)) return null;

        // Group “same address” pins more forgivingly (6 decimals was too strict)
        // 4 decimals ≈ ~11m lat resolution
        const key = `${pos.lat.toFixed(4)},${pos.lng.toFixed(4)}`;
        return { id, key, pos };
      })
      .filter(Boolean);

    // 2) Bucket by (roughly) identical coords
    const buckets = new Map();
    for (const item of base) {
      if (!buckets.has(item.key)) buckets.set(item.key, []);
      buckets.get(item.key).push(item);
    }

    // 3) For buckets with >1 team, fan them out in a small ring
    // Make the offset clearly visible at typical “city” zoom levels
    const step = 0.0025; // ≈ ~278m latitude

    for (const [, list] of buckets.entries()) {
      if (list.length === 1) {
        out.set(list[0].id, list[0].pos);
        continue;
      }

      // stable ordering so pins don’t “shuffle” between renders
      list.sort((a, b) => a.id - b.id);

      const n = list.length;
      for (let i = 0; i < n; i++) {
        const angle = (2 * Math.PI * i) / n;

        const baseLat = list[i].pos.lat;
        const latRad = (baseLat * Math.PI) / 180;
        const cosLat = Math.max(0.2, Math.cos(latRad)); // clamp to avoid extreme division

        const dy = Math.sin(angle) * step;
        const dx = (Math.cos(angle) * step) / cosLat; // meter-correct longitude

        out.set(list[i].id, {
          lat: baseLat + dy,
          lng: list[i].pos.lng + dx,
        });
      }
    }


    return out;
  }, [nearbyTeams, teamGeoOverride]);



  // mirror to global store (Evaluate Teams slice)
  const setNearbyTeamsLocal = useTeamStore((s) => s.setNearbyTeamsLocal);
  const setTeamGeoOverrideLocal = useTeamStore((s) => s.setTeamGeoOverrideLocal);


// Hydrate local Evaluate Teams state from store (if fresh within 10 min)
useEffect(() => {
  const snap = useTeamStore.getState();
  const fresh = snap.updatedAtLocal && (Date.now() - snap.updatedAtLocal) < 10 * 60 * 1000;

  if (!fresh) return;

  if (Array.isArray(snap.nearbyTeamsLocal) && snap.nearbyTeamsLocal.length && nearbyTeams.length === 0) {
    setNearbyTeams(snap.nearbyTeamsLocal);
  }

  if (Array.isArray(snap.teamGeoOverrideLocal) && teamGeoOverride.size === 0 && snap.teamGeoOverrideLocal.length) {
    setTeamGeoOverride(new Map(snap.teamGeoOverrideLocal));
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);



  // Address geocode overrides for candidates (u_id -> {lat,lng})
  const [candidateGeoOverride, setCandidateGeoOverride] = useState(new Map());

  // highlight the teammate whose card is hovered
  const [hoverUserId, setHoverUserId] = useState(null);
  const [hoverTeamId, setHoverTeamId] = useState(null);


  const normalizeZip = (z) => {
    const s = (z ?? '').toString().replace(/\D/g, '').slice(0, 5);
    return s.length === 5 ? s : null;
  };

  // Refs for map and circle
  const mapRef = useRef(null);
  const circleRef = useRef(null);
  const hasFitRef = useRef(false);
  // Google Maps JS ready flag (set true in <GoogleMap onLoad>)
  const [gReady, setGReady] = useState(false);

  // Map UI toggles (2A)
  const [showCandidates, setShowCandidates] = useState(false);
  // Service Radius is always on; the overlay is mutually exclusive
  const [activeLayer, setActiveLayer] = useState('candidates');
  const isLocal      = activeLayer === 'local' || activeLayer === 'teams'; // Local Teams (alias)

  // DEBUG: verify micro-offset bucketing + whether offsets are actually applied
  useEffect(() => {
    // Only log while in Join Teams / Local Teams view, otherwise this will be noisy.
    if (!isLocal) return;

    const teams = Array.isArray(nearbyTeams) ? nearbyTeams : [];
    const totalTeams = teams.length;

    // Rebuild the same "base" items your useMemo uses (keyed coords)
    const base = teams
      .map((t) => {
        const id = Number(t?.team_id ?? t?.id);
        if (!Number.isFinite(id)) return null;

        const ovr = teamGeoOverride.get(id);
        const pos = ovr
          ? { lat: ovr.lat, lng: ovr.lng }
          : (t?.b_lat != null && t?.b_lng != null) ? { lat: +t.b_lat, lng: +t.b_lng }
          : (t?.lat != null && t?.lng != null) ? { lat: +t.lat, lng: +t.lng }
          : null;

        if (!pos || !Number.isFinite(pos.lat) || !Number.isFinite(pos.lng)) return null;

        const key = `${pos.lat.toFixed(4)},${pos.lng.toFixed(4)}`;
        return { id, key, pos, src: ovr ? 'ovr' : (t?.b_lat != null ? 'db' : (t?.lat != null ? 'srv' : 'unknown')) };
      })
      .filter(Boolean);

    const validCoords = base.length;

    const buckets = new Map();
    for (const item of base) {
      if (!buckets.has(item.key)) buckets.set(item.key, []);
      buckets.get(item.key).push(item);
    }

    const bucketSizes = Array.from(buckets.values()).map((arr) => arr.length);
    const bucketCount = bucketSizes.length;
    const maxBucket = bucketSizes.length ? Math.max(...bucketSizes) : 0;
    const stackedBuckets = bucketSizes.filter((n) => n > 1).length;
    const stackedTeams = bucketSizes.filter((n) => n > 1).reduce((sum, n) => sum + n, 0);

    // how many teams actually have an offset pos computed?
    let offsetApplied = 0;
    let missingInMap = 0;
    for (const item of base) {
      if (teamPinPosById.has(item.id)) offsetApplied++;
      else missingInMap++;
    }

    // show the worst (largest) bucket so we can see if those are really grouping
    let worstKey = null;
    let worstList = [];
    for (const [k, list] of buckets.entries()) {
      if (list.length > worstList.length) { worstKey = k; worstList = list; }
    }

    console.log('[JoinTeams micro-offset]', {
      totalTeams,
      validCoords,
      bucketCount,
      stackedBuckets,
      stackedTeams,
      maxBucket,
      offsetApplied,
      missingInMap,
      worstKey,
      worstIds: worstList.map((x) => x.id),
      worstSrcs: worstList.map((x) => x.src),
    });
  }, [isLocal, nearbyTeams, teamGeoOverride, teamPinPosById]);
   // — Recruiting team-pick mini modal (for users with 2+ teams) —
  const [teamPickOpen, setTeamPickOpen] = useState(false);
  const [teamPickList, setTeamPickList] = useState([]);
  const [teamPickSelected, setTeamPickSelected] = useState(null); // radio (single)

  // The one true click handler for the “Recruit Teammates” button
  const handleRecruitClick = async () => {
    // 1) Switch focus to "Recruit Teammates" so Column 3 shows candidates
    setActiveLayer('candidates');
    setShowCandidates?.(true);

    // 2) Resolve viewer id from profile store first (authoritative), then fallback to localStorage
    let uid = Number(profileUser?.u_id) || null;
    if (!uid) {
      uid = Number(window.localStorage.getItem('u_id')) || null;
    } else {
      // keep RecruitingModal happy if it reads from localStorage
      try { window.localStorage.setItem('u_id', String(uid)); } catch {}
    }

    if (!uid) {
      // No logged-in user id available — just open the shell (it will no-op load)
      openRecruiting();
      return;
    }

    // 3) Load the teams this viewer is on (captain or teammate)
    try {
      const res = await fetch(`/api/teams/recruiting/debug/my-teams?u_id=${uid}`, { credentials: 'include' });
      if (!res.ok) throw new Error(`GET my-teams ${res.status}`);
      const json = await res.json();
      const teams = Array.isArray(json?.teams) ? json.teams : [];

      if (teams.length === 0) {
        // No teams → send them to activate/create
        if (typeof openActivate === 'function') openActivate();
        return;
      }

      if (teams.length === 1) {
        // Single team → select & load the list, then open the modal
        const tid = Number(teams[0].team_id);
        setTeamId(tid);
        await loadTeamList(tid);
        openRecruiting();
        return;
      }

      // 2+ teams → ask which team they’re recruiting for
      setTeamPickList(teams);
      setTeamPickSelected(Number(teams[0].team_id)); // default to first
      setTeamPickOpen(true);
    } catch (e) {
      console.warn('[TeamInfo] Failed to load my teams for recruiting:', e);
      // Fallback: still open the modal; user can pick later inside it if needed
      openRecruiting();
    }
  };

// --- Drag & Drop onto team buttons (from LocalUsersTeamList / CandidateListModal) ---
const DRAG_MIME = 'application/ehandshake-candidate';
const [dragTargetTeamId, setDragTargetTeamId] = useState(null);

// Always preventDefault on dragover so the browser allows dropping.
// Also set a friendly dropEffect and light up the ring.
const onDragOverTeam = (teamId) => (e) => {
  if (!e) return;
  e.preventDefault();
  try { e.dataTransfer.dropEffect = 'copy'; } catch {}
  setDragTargetTeamId(Number(teamId));
};

const onDragEnterTeam = (teamId) => (e) => {
  if (!e) return;
  e.preventDefault();
  setDragTargetTeamId(Number(teamId));
};

const onDragLeaveTeam = () => setDragTargetTeamId(null);

const onDropOnTeam = (teamId) => async (e) => {
  e.preventDefault?.();
  setDragTargetTeamId(null);

  try {
    const payload = e.dataTransfer.getData(DRAG_MIME);
    if (!payload) return;
    const data = JSON.parse(payload);
    const candidate_u_id = Number(data?.candidate_u_id);
    if (!Number.isFinite(candidate_u_id)) return;

    const viewer = Number(window.localStorage.getItem('u_id')) || null;
    if (!viewer) return;

    // Seed otherUsersStore immediately (optional best-effort)
    try {
      const pool = (window.__teamStore?.getState?.().localUsers || []);
      const lu = Array.isArray(pool) ? pool.find(u => Number(u.u_id) === candidate_u_id) : null;
      if (lu) window.__otherUsersStore?.getState?.().upsertUsers?.([lu]);
    } catch {}

    // Persist to server (API already dedupes with ON CONFLICT DO NOTHING)
    const res = await fetch('/api/teams/recruiting/items', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        team_id: Number(teamId),
        added_by_u_id: Number(viewer),
        candidate_u_ids: [candidate_u_id],
      }),
      credentials: 'include',
    });
    if (!res.ok) throw new Error(`POST /items ${res.status}`);

    // Load shortlist for this team and open modal showing the just-added candidate
    const { setTeamId, loadTeamList } = useRecruitingStore.getState();
    setTeamId(Number(teamId));
    await loadTeamList(Number(teamId));
    useModalStore.getState().openRecruiting();
  } catch (err) {
    console.error('[TeamInfo] drop onto team failed:', err);
  }
};


  // --- Draft team activation (Column 1B) ---
  const [draftPanelOpen, setDraftPanelOpen] = useState(false);
  const [draftTeam, setDraftTeam] = useState(null); // { team_id, is_active, team_name, owner_u_id, area_hint }
  const [teamsLoaded, setTeamsLoaded] = useState(false); // ← NEW
  const [proposedName, setProposedName] = useState('');
  const [checking, setChecking] = useState(false);
  const [available, setAvailable] = useState(null); // true | false | null
  const [suggestions, setSuggestions] = useState([]);   // availability checker suggestions
  const [lockAvailability, setLockAvailability] = useState(false); // ← freeze availability suggestions after user picks
  const [pickedOnce, setPickedOnce] = useState(false);            // ← NEW: entered post-pick edit mode
  const [areaNames, setAreaNames] = useState([]);       // local area names for instructions
    // Mascots (from DB)
  const [mascots, setMascots] = useState([]);

  // Return a mascot name from DB list, or '' if none loaded yet
  function mascotAt(idx) {
    if (!Array.isArray(mascots) || mascots.length === 0) return '';
    return mascots[idx % mascots.length];
  }
  const [activating, setActivating] = useState(false);
  const viewerId = Number(profileUser?.u_id) || null;
  const [loadingAreas, setLoadingAreas] = useState(false);
  // micro-dock state (inline edit/delete)
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);
  const [renameError, setRenameError] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);




// Label: show real team name ONLY if not a draft-like name; otherwise "Team <last>"
const nameStr = String(draftTeam?.team_name || '');
const isRealName =
  !!nameStr && !/^draft\b/i.test(nameStr) && !/^team\s/i.test(nameStr);

const teamButtonLabel = !teamsLoaded
  ? 'Loading team…'
  : (draftTeam?.is_active && isRealName
      ? nameStr
      : `Team ${String(profileUser?.u_last_name || '').trim() || 'Name'}`);






  // lazy-create or get the viewer's draft team
  async function fetchOrCreateDraft(areaHint) {
    if (!viewerId) return;
    const p = new URLSearchParams();
    p.set('u_id', viewerId);
    if (areaHint) p.set('area_hint', areaHint);
    const res = await fetch(`/api/teams/recruiting/draft/create?${p.toString()}`, { credentials: 'include' });
    const json = await res.json();
    if (res.ok && json?.ok) {
      setDraftTeam(json.team);
      return json.team;
    }
    throw new Error('create draft failed');
  }

  // check name availability (debounced with ref)
  const checkTimerRef = useRef(null);
  function onNameChange(v) {
    setProposedName(v);
    setAvailable(null);
    setSuggestions([]);

    if (checkTimerRef.current) clearTimeout(checkTimerRef.current);

    const name = v.trim();
    if (!name) return;

    checkTimerRef.current = setTimeout(async () => {
      try {
        setChecking(true);
        const r = await fetch(`/api/teams/recruiting/draft/check-name?name=${encodeURIComponent(name)}`, { credentials: 'include' });
        const j = await r.json();
        if (r.ok) {
          setAvailable(Boolean(j.available));

          const raw = Array.isArray(j.suggestions) ? j.suggestions : [];
          let filtered = raw.filter(s => !/\bHeights\b/i.test(String(s)));

          // In post-pick edit mode, strip compass words AND Heights from availability suggestions
          if (pickedOnce) {
            filtered = filtered.map(s =>
              String(s)
                .replace(/\b(Central|East|West|North|South|Heights)\b/gi, '')
                .replace(/\s{2,}/g, ' ')
                .trim()
            );
          }

          // De-duplicate case-insensitively
          const uniq = [];
          const seen = new Set();
          for (const s of filtered) {
            const k = String(s).toLowerCase();
            if (!seen.has(k)) { seen.add(k); uniq.push(s); }
          }

          if (pickedOnce) {
            // In post-pick mode, show at most ONE suggestion,
            // and only if it's different from the current input
            const inputKey = name.toLowerCase();
            const alt = uniq.find(u => u.toLowerCase() !== inputKey);
            setSuggestions(alt ? [alt] : []);
          } else {
            // Pre-pick: keep up to 5 uniques (render still handles mascot rules)
            setSuggestions(uniq.slice(0, 5));
          }


        }
      } finally {
        setChecking(false);
      }
    }, 350);
  }

  // Clean up any pending timer on unmount
  useEffect(() => {
    return () => {
      if (checkTimerRef.current) clearTimeout(checkTimerRef.current);
    };
  }, []);

  // Prep the RegisterTeamModal when it opens (reset fields + load areas/mascots)
  useEffect(() => {
    if (!registerOpen) return;

    // 1) Reset state just like openActivate(), but do NOT open the inline panel
    setPickedOnce(false);
    setLockAvailability(false);
    setAvailable(null);
    setSuggestions([]);
    if (checkTimerRef.current) clearTimeout(checkTimerRef.current);

    setDraftTeam(null); // break tie to any active/draft

    // 2) Fresh prefill for proposedName every time
    let nextName = '';
    if (profileUser?.b_city) {
      nextName = String(profileUser.b_city).trim();
    } else if (profileUser?.b_zip_code) {
      const zip5 = String(profileUser.b_zip_code).replace(/\D/g, '').slice(0, 5);
      nextName = `Area ${zip5}`;
    }
    onNameChange(nextName);

    // 3) Load helpers (areas + mascots)
    (async () => {
      setLoadingAreas(true);
      setAreaNames([]);

      // mascots (independent)
      try {
        const mr = await fetch('/api/teams/recruiting/mascots?limit=50', { credentials: 'include' });
        const mj = await mr.json();
        if (mr.ok && mj?.ok && Array.isArray(mj.mascots) && mj.mascots.length) {
          setMascots(mj.mascots);
        }
      } catch {}

      try {
        await loadAreasBackground();
      } finally {
        setLoadingAreas(false);
      }
    })();
  }, [registerOpen]); 

  // When the RegisterTeamModal closes (user cancelled), restore UI state so no temporary team shows.
useEffect(() => {
  if (registerOpen) return; // only run on close

  // Collapse any inline “activate” UI
  setDraftPanelOpen(false);

  // Clear any temporary suggestion/checking state
  if (checkTimerRef.current) clearTimeout(checkTimerRef.current);
  setPickedOnce(false);
  setLockAvailability(false);
  setAvailable(null);
  setSuggestions([]);
  setProposedName('');

  // Restore draftTeam to the currently active team (or first team), or to null if none
  const active = Array.isArray(myTeams)
    ? (myTeams.find(t => t?.is_active) || myTeams[0] || null)
    : null;

  if (active) {
    setDraftTeam({
      team_id: Number(active.team_id),
      team_name: active.team_name || null,
      is_active: !!active.is_active,
    });
  } else {
    setDraftTeam(null);
  }
}, [registerOpen, myTeams]);

  // Open the activation panel for a NEW team cycle (never reuse active team)
  async function openActivate() {
    try {
      // 1) Hard reset any lingering state from a previous/active team
      setPickedOnce(false);
      setLockAvailability(false);
      setAvailable(null);
      setSuggestions([]);
      if (checkTimerRef.current) clearTimeout(checkTimerRef.current);

      // IMPORTANT: break the tie to the currently-active team
      setDraftTeam(null);

      // 2) Fresh prefill for proposedName every time
      let nextName = '';
      if (profileUser?.b_city) {
        nextName = String(profileUser.b_city).trim();
      } else if (profileUser?.b_zip_code) {
        const zip5 = String(profileUser.b_zip_code).replace(/\D/g, '').slice(0, 5);
        nextName = `Area ${zip5}`;
      }
      setProposedName(nextName);

      // 3) Open panel & fetch helpers
      setLoadingAreas(true);
      setAreaNames([]);
      setDraftPanelOpen(true);

      // Fetch mascots (independent)
      try {
        const mr = await fetch('/api/teams/recruiting/mascots?limit=50', { credentials: 'include' });
        const mj = await mr.json();
        if (mr.ok && mj?.ok && Array.isArray(mj.mascots) && mj.mascots.length) {
          setMascots(mj.mascots);
        }
      } catch {
        /* ignore mascot errors */
      }

      // Load areas in the background — always stop spinner
      try {
        await loadAreasBackground();
      } finally {
        setLoadingAreas(false);
      }
    } catch (e) {
      console.error('openActivate failed', e);
      setLoadingAreas(false);
    }
  }



  // NEW FUNCTION: Separate area loading
  async function loadAreasBackground() {
    try {
      let areasLoaded = false;
      const bId = Number(profileUser?.b_id);

      // 1) Try to load stored local areas
      if (Number.isFinite(bId) && bId > 0) {
        try {
          const res = await fetch(`/api/teams/recruiting/local-areas?b_id=${bId}&limit=5`, { credentials: 'include' });
          const j = await res.json();
          if (res.ok && j?.ok && Array.isArray(j.areas) && j.areas.length) {
            setAreaNames(j.areas);
            setLoadingAreas(false);
            areasLoaded = true;
      
            // Auto-set team name from first area if we don't have a city OR if we're using zip code placeholder
            if ((!profileUser?.b_city || proposedName?.includes('Area ') || proposedName === 'Loading area...') && (!proposedName || proposedName === 'Loading area...' || proposedName?.startsWith('Area '))) {
              const firstArea = String(j.areas[0]).split(',')[0].trim();
              setProposedName(firstArea);
              return true; // Return success
            }
            return true; // Return success
          }
        } catch (err) {
          console.warn('local-areas load failed', err);
        }
      }

      // 2) If no stored areas, fetch from Google
      if (!areasLoaded) {
        let lat = Number(profileUser?.b_lat);
        let lng = Number(profileUser?.b_lng);

        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
          if (userCenter && Number.isFinite(userCenter.lat) && Number.isFinite(userCenter.lng)) {
            lat = userCenter.lat;
            lng = userCenter.lng;
          }
        }

        if (Number.isFinite(lat) && Number.isFinite(lng) && Number.isFinite(bId) && bId > 0) {
          try {
            const res2 = await fetch(
              `/api/teams/recruiting/local-areas/fetch?b_id=${bId}&lat=${lat}&lng=${lng}&max=10`,
              { method: 'POST', credentials: 'include' }
            );
            const j2 = await res2.json();
            if (res2.ok && j2?.ok) {
              // SUCCESS: Areas were fetched from Google and stored in database
              // Now we need to RE-FETCH the stored areas to display them
              try {
                const res3 = await fetch(`/api/teams/recruiting/local-areas?b_id=${bId}&limit=5`, { credentials: 'include' });
                const j3 = await res3.json();
                if (res3.ok && j3?.ok && Array.isArray(j3.areas) && j3.areas.length) {
                  setAreaNames(j3.areas);
                  
                  // Auto-set team name from first area if we don't have a city
                  if (!profileUser?.b_city && (!proposedName || proposedName === 'Loading area...')) {
                    const firstArea = String(j3.areas[0]).split(',')[0].trim();
                    setProposedName(firstArea);
                  }
                  return true; // Return success
                } else if (Array.isArray(j2.areas) && j2.areas.length) {
                  // Fallback: use the areas from the Google response directly
                  setAreaNames(j2.areas.slice(0, 5));
                  
                  // Auto-set team name from first area if we don't have a city
                  if (!profileUser?.b_city && (!proposedName || proposedName === 'Loading area...')) {
                    const firstArea = String(j2.areas[0]).split(',')[0].trim();
                    setProposedName(firstArea);
                  }
                  return true; // Return success
                }
              } catch (err) {
                console.warn('Failed to re-fetch stored areas', err);
                // Fallback: use the areas from the Google response directly
                if (Array.isArray(j2.areas) && j2.areas.length) {
                  setAreaNames(j2.areas.slice(0, 5));
                  
                  // Auto-set team name from first area if we don't have a city
                  if (!profileUser?.b_city && (!proposedName || proposedName === 'Loading area...')) {
                    const firstArea = String(j2.areas[0]).split(',')[0].trim();
                    setProposedName(firstArea);
                  }
                  return true; // Return success
                }
              }
            }
          } catch (err) {
            console.warn('Google areas fetch failed', err);
          }
        }
      }

      setLoadingAreas(false);
      
      // Final fallback if no areas were found at all
      if (!profileUser?.b_city && (!proposedName || proposedName === 'Loading area...')) {
        if (profileUser?.b_zip_code) {
          const zip5 = String(profileUser.b_zip_code).replace(/\D/g, '').slice(0, 5);
          setProposedName(`Area ${zip5}`);
        } else {
          setProposedName('My Team');
        }
      }
      
      return false; // Return failure
    } catch (error) {
      console.error('loadAreasBackground failed:', error);
      setLoadingAreas(false);
      
      // Fallback on error
      if (!profileUser?.b_city && (!proposedName || proposedName === 'Loading area...')) {
        if (profileUser?.b_zip_code) {
          const zip5 = String(profileUser.b_zip_code).replace(/\D/g, '').slice(0, 5);
          setProposedName(`Area ${zip5}`);
        } else {
          setProposedName('My Team');
        }
      }
      
      return false; // Return failure
    }
  }


  // Create a draft (if needed) and activate it — guarantees a fresh draft for new teams
  async function activateNow() {
    if (!viewerId || !proposedName.trim()) return;

    try {
      setActivating(true);

      // If draftTeam points to an already ACTIVE team, discard it and force a new draft
      let t = draftTeam;
      if (t?.is_active) {
        t = null;
        setDraftTeam(null);
      }

      // Ensure we have a draft
      if (!t?.team_id) {
        const areaHint =
          (profileUser?.b_zip ? String(profileUser.b_zip) : null) ||
          (profileUser?.b_zip_code
            ? String(profileUser.b_zip_code).replace(/\D/g, '').slice(0, 5)
            : null);
        t = await fetchOrCreateDraft(areaHint);
        if (!t?.team_id) throw new Error('Draft creation failed');
        setDraftTeam(t);
      }

      // Activate with the chosen name
      const url = `/api/teams/recruiting/draft/activate?team_id=${t.team_id}&u_id=${viewerId}&name=${encodeURIComponent(proposedName.trim())}`;
      const res = await fetch(url);
      const json = await res.json();

      if (res.ok && json?.ok) {
        const activated = json.team;
        setDraftTeam(activated);
        hydrateTeams([activated]); // recompute store (isCaptain + activeTeamId)
        setActiveTeamId(activated.team_id);
        setDraftPanelOpen(false);

        // 🔒 CLOSE the RegisterTeamModal on successful activation
        setRegisterOpen(false);

        requestAnimationFrame(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        });
      } else {
        alert(json?.error || 'Activation failed');
      }
    } catch (e) {
      console.error('activateNow failed', e);
    } finally {
      setActivating(false);
    }
  }




  // Rename active team (inline) — guarded + friendly errors + no double submit
  async function saveRename() {
    const current = String(draftTeam?.team_name || '').trim();
    const name = (renameValue || '').trim();

    if (!draftTeam?.team_id || !viewerId) return;
    if (!name || name === current) return;     // empty or unchanged
    if (renameSaving) return;                   // prevent double submit

    setRenameError('');
    setRenameSaving(true);
    try {
      // 1) pre-check availability so duplicates are blocked deterministically
      const chk = await fetch(`/api/teams/recruiting/draft/check-name?name=${encodeURIComponent(name)}`, { credentials: 'include' });
      const chkJ = await chk.json().catch(() => ({}));
      if (!chk.ok || chkJ?.available === false) {
        setRenameError('That team name is already taken.');
        setRenameSaving(false);
        return;
      }

      // 2) proceed with rename
      const url = `/api/teams/recruiting/team/rename?team_id=${draftTeam.team_id}&u_id=${viewerId}&name=${encodeURIComponent(name)}`;
      const res = await fetch(url, { method: 'POST', credentials: 'include' });
      const json = await res.json().catch(() => ({}));

      if (!res.ok || !json?.ok || !json.team) {
        const code = json?.error || 'server_error';
        let msg = 'Rename failed.';
        if (code === 'name_taken') msg = 'That team name is already taken.';
        else if (code === 'forbidden_not_owner') msg = 'Only the captain can rename this team.';
        else if (code === 'name_too_long') msg = 'Name is too long.';
        else if (code === 'team_not_found') msg = 'Team not found.';
        setRenameError(msg);
        return;
      }


      const t = json.team;
      setDraftTeam(t);
      hydrateTeams([t]);
      setRenaming(false);
      setRenameValue('');
      setRenameError('');
    } catch (e) {
      console.error('saveRename failed', e);
      setRenameError('Network error. Please try again.');
    } finally {
      setRenameSaving(false);
    }
  }


  // Delete team (active OR draft)
  async function performDelete() {
    if (!draftTeam?.team_id || !viewerId) return;
    try {
      const url = `/api/teams/recruiting/team/delete?team_id=${draftTeam.team_id}&u_id=${viewerId}`;
      const res = await fetch(url, { method: 'DELETE', credentials: 'include' });
      const json = await res.json();
      if (res.ok && json?.ok) {
        // remove team locally; user may still create another draft later
        // ...
        setConfirmingDelete(false);
        setRenaming(false);
        setRenameValue('');
      } else {
        alert(json?.error || 'Delete failed');
      }
    } catch (e) {
      console.error('performDelete failed', e);
    }
  }

    // ▼ FIX: deterministic team hydrate (active-first + saved-ID + force-active fallback)
    useEffect(() => {
      const uid = viewerId;
      if (!uid) return;
      let cancelled = false;

      (async () => {
        try {
          const res = await fetch(`/api/teams/recruiting/debug/my-teams?u_id=${uid}`, { credentials: 'include' });
          const j = await res.json();

          // 1) normalize
          const raw = Array.isArray(j?.teams) ? j.teams : [];
          let norm = raw.map(t => ({
            ...t,
            team_id: Number(t?.team_id),
            is_active: !!(t && (t.is_active === true || t.is_active === 1 || t.is_active === '1' || t.is_active === 'true' || t.is_active === 't'))
          }));

          // 2) if none are active but:
          //    - we have a saved activeTeamId, OR
          //    - there's exactly one team,
          //    then force that team to active for the UI (server may lag on flag).
          const savedId = Number(activeTeamId);
          if (!norm.some(t => t.is_active)) {
            if (Number.isFinite(savedId)) {
              norm = norm.map(t => t.team_id === savedId ? { ...t, is_active: true } : t);
            } else if (norm.length === 1) {
              norm = [{ ...norm[0], is_active: true }];
            }
          }

          // 3) sort active first
          norm.sort((a, b) => (b.is_active === true) - (a.is_active === true));

          // 4) choose: saved → any active → first
          const bySaved = Number.isFinite(savedId) ? norm.find(t => t.team_id === savedId) : null;
          const chosen  = bySaved || norm.find(t => t.is_active) || norm[0] || null;

          if (cancelled) return;

          // 5) persist
          setMyTeams(norm);
          if (chosen?.team_id) setActiveTeamId(chosen.team_id);

          // 6) drive local UI
          if (chosen) {
            setDraftTeam({
              team_id: chosen.team_id,
              team_name: chosen.team_name || null,
              // keep true if we forced it above
              is_active: !!chosen.is_active
            });
            setDraftPanelOpen(false);
          }
        } catch {
          // ignore
        } finally {
          if (!cancelled) setTeamsLoaded(true);
        }
      })();

      return () => { cancelled = true; };
    }, [viewerId, activeTeamId, setMyTeams, setActiveTeamId]);





  // Data
  const [userCenter, setUserCenter] = useState(null);
  const [customers, setCustomers] = useState([]);     // [{id, lat, lng, zip}]
  const [customersLoading, setCustomersLoading] = useState(true);
  const [candidates] = useState([]);   // [{id, lat, lng, zip}]
  const [showBizTip, setShowBizTip] = useState(false);
  const [overInfo, setOverInfo] = useState(false);
  const closeTimer = useRef(null);


  useEffect(() => {
    console.log('Business fields check:', {
      b_name: profileUser?.b_name,
      b_business_industry: profileUser?.b_business_industry,
      b_service_distance: profileUser?.b_service_distance, // This should exist!
      has_b_service_distance: !!profileUser?.b_service_distance
    });
  }, [profileUser]);  
  // Always mirror the store’s current distance into local UI state, as a number
  useEffect(() => {
    const next = Number(getServiceDistance());
    if (Number.isFinite(next) && next !== serviceDistance) {
      setServiceDistance(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.b_service_distance, profile?.business?.b_service_distance]);

  




  // Ensure refresh runs once when u_id appears after rehydration
  useEffect(() => {
    const id = profileUser?.u_id;
    if (!id) return;

    (async () => {
      await refreshProfile(id);

      // Snap local UI state to the store’s current distance (numeric)
      const next = Number(getServiceDistance());
      if (Number.isFinite(next)) {
        setServiceDistance(next);
      }
    })();
  }, [profileUser?.u_id]);

  useEffect(() => {
    if (profileUser?.u_id) {
      try { window.localStorage.setItem('u_id', String(profileUser.u_id)); } catch {}
    }
  }, [profileUser?.u_id]);


  const [showDistanceModal, setShowDistanceModal] = useState(false);
  const [pendingDistance, setPendingDistance] = useState(serviceDistance);
  const [savingDistance, setSavingDistance] = useState(false);
  const isOwner = (profileUser?.u_role || '').toLowerCase() === 'owner';


  function openDistanceModal() {
    setPendingDistance(serviceDistance);
    setShowDistanceModal(true);
  }

async function handleSaveDistance() {
  if (!isOwner) return;
  const newMiles = Math.max(1, Math.min(500, parseInt(pendingDistance, 10) || 0));

  try {
    setSavingDistance(true);

    const res = await axios.post(
      '/api/profile/teams/service-distance',
      { u_id: profileUser?.u_id, distance: newMiles },
      { withCredentials: true }
    );

    if (res?.status === 200 && res?.data?.ok) {
      // 1) Update local UI state
      setServiceDistance(newMiles);

      // 2) Single source of truth: update the Zustand profile
      updateProfile({
        b_service_distance: newMiles,
        business: { b_service_distance: newMiles }
      });

      // 3) Refit map and close modal
      hasFitRef.current = false;
      setShowDistanceModal(false);
    } else {

      console.error('Unexpected response from service-distance:', res?.status, res?.data);
    }
  } catch (err) {
    console.error('Failed to update distance:', err);
  } finally {
    setSavingDistance(false);
  }
}



  // Display strings
  const displayName =
    [profileUser?.u_first_name, profileUser?.u_last_name].filter(Boolean).join(' ').trim() || 'You';

  const businessName = (profileUser?.b_name ?? '').toString().trim();

  const addrLine1 = (profileUser?.b_address ?? '').toString().trim();
  const addrLine2 = [
    (profileUser?.b_city  ?? '').toString().trim(),
    (profileUser?.b_state ?? '').toString().trim(),
    ((profileUser?.b_zip_code ?? '').toString().replace(/[^0-9]/g, '').slice(0, 5))
  ].filter(Boolean).join(', ');

  // Red pin icon w/ anchor + 8×8 clickable shape around the tip
  function redPinIcon() {
    const g = window.google?.maps;
    if (g) {
      return {
        url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
        size: new g.Size(32, 32),
        scaledSize: new g.Size(32, 32),
        anchor: new g.Point(16, 32), // tip of the pin
      };
    }
    return 'https://maps.google.com/mapfiles/ms/icons/red-dot.png';
  }
  const redPinShape = { coords: [0, 0, 32, 32], type: 'rect' }; // full icon bounds


  // service radius (m)
  const serviceMeters = Math.max(((serviceDistance ?? 10) * 1609.34) | 0, 1609);
  useEffect(() => {
    if (mapRef.current && circleRef.current) {
      const bounds = circleRef.current.getBounds?.();
      if (bounds) {
        mapRef.current.fitBounds(bounds, { top: 16, right: 16, bottom: 16, left: 16 });
      }
    }
  }, [serviceDistance, userCenter]);

  // --- distance + tooltip helpers (miles; used for Marker.title) ---
function distanceMiles(lat1, lng1, lat2, lng2) {
  const toRad = (x) => (x * Math.PI) / 180;
  const R = 6371000; // meters
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const meters = R * c;
  return meters / 1609.34; // miles
}

function markerTitleForCustomer(c, userCenter) {
  const name =
    [c?.first_name, c?.last_name].filter(Boolean).join(' ').trim() || 'Customer';
  if (userCenter && c?.lat != null && c?.lng != null) {
    const mi = distanceMiles(userCenter.lat, userCenter.lng, +c.lat, +c.lng);
    return `${name} — ${mi.toFixed(1)} mi`;
  }
  return name;
}


  // ZIP → lat/lng fallback (lightweight, local file you host at /data/zip-centroids.min.json)
  let ZIP_DICT = null;
  async function zipToLatLng(zip) {
    const z = String(zip || '').slice(0, 5);
    if (!z) return null;
    if (!ZIP_DICT) {
      try {
        const resp = await fetch('/data/zip-centroids.min.json');
        ZIP_DICT = await resp.json(); // { "94103":[37.7725,-122.4091], ...}
      } catch {
        return null;
      }
    }
    const hit = ZIP_DICT[z];
    return hit ? { lat: hit[0], lng: hit[1] } : null;
  }
// Geocode a full street address (cached). Keep it simple & reliable.
const geocodeCache = useRef(new Map());
async function geocodeAddress({ street, city, state, zip }) {
  const zip5 = (zip || '').toString().replace(/\D/g, '').slice(0, 5);
  const addressStr = [String(street || '').trim(), String(city || '').trim(), String(state || '').trim(), zip5]
    .filter(Boolean)
    .join(', ');
  const key = addressStr;
  if (!key) return null;

  if (geocodeCache.current.has(key)) return geocodeCache.current.get(key);

  const g = window.google?.maps;
  if (!g?.Geocoder) return null;

  // 1) Try the full address string (most robust)
  const result = await new Promise((resolve) => {
    const geocoder = new g.Geocoder();
    geocoder.geocode({ address: addressStr }, (results, status) => {
      if (status === 'OK' && results?.[0]?.geometry?.location) {
        const loc = results[0].geometry.location;
        resolve({ lat: loc.lat(), lng: loc.lng() });
      } else {
        resolve(null);
      }
    });
  });

  // 2) If that fails, try a minimal “street + ZIP” form
  let finalResult = result;
  if (!finalResult && String(street || '').trim() && zip5) {
    finalResult = await new Promise((resolve) => {
      const geocoder = new g.Geocoder();
      geocoder.geocode({ address: `${String(street).trim()}, ${zip5}` }, (results, status) => {
        if (status === 'OK' && results?.[0]?.geometry?.location) {
          const loc = results[0].geometry.location;
          resolve({ lat: loc.lat(), lng: loc.lng() });
        } else {
          resolve(null);
        }
      });
    });
  }

  geocodeCache.current.set(key, finalResult);
  return finalResult;
}




// resolve user center (DB coords → ZIP centroid → ADDRESS geocode; no US fallback)
useEffect(() => {
  (async () => {
    const zip5 = (profileUser?.b_zip_code || '').toString().replace(/\D/g, '').slice(0, 5);
    const street = (profileUser?.b_address || '').toString().trim();
    const city   = (profileUser?.b_city || '').toString().trim();
    const state  = (profileUser?.b_state || '').toString().trim();

    // 1) Exact business coords (DB) if present (fast, no Maps API needed)
    if (profileUser?.b_lat != null && profileUser?.b_lng != null) {
      setUserCenter({ lat: +profileUser.b_lat, lng: +profileUser.b_lng });
      return;
    }

    // 2) ZIP centroid (fast local lookup)
    if (zip5) {
      const byZip = await zipToLatLng(zip5);
      if (byZip) {
        setUserCenter(byZip);
        return;
      }
    }

    // 3) Full address geocode (requires Google Maps Geocoder)
    if (street && (city || state || zip5)) {
      const byAddr = await geocodeAddress({ street, city, state, zip: zip5 });
      if (byAddr) {
        setUserCenter(byAddr);
        return;
      }
    }

    // 4) No fallback to US center — keep userCenter null so the map doesn’t mount and flicker.
    // The GoogleMap only renders when userCenter is truthy, so no Kansas flash.
  })();
}, [
  profileUser?.b_address,
  profileUser?.b_city,
  profileUser?.b_state,
  profileUser?.b_zip_code,
  profileUser?.b_lat,
  profileUser?.b_lng
]);






// fetch customers once; fill lat/lng from zip if missing
useEffect(() => {
  let cancel = false;
  (async () => {
    try {
      if (!cancel) setCustomersLoading(true);

      const uid = profileUser?.u_id;
      if (!uid) { if (!cancel) { setCustomers([]); setCustomersLoading(false); } return; }

      const resp = await fetch(`/api/profile/customers/geo?u_id=${uid}`, { credentials: 'include' });
      if (!resp.ok) { if (!cancel) { setCustomers([]); setCustomersLoading(false); } return; }

      const ct = resp.headers.get('content-type') || '';
      if (!ct.includes('application/json')) {
        if (!cancel) { setCustomers([]); setCustomersLoading(false); }
        return;
      }

const rows = await resp.json();


      const enriched = await Promise.all(
        rows.map(async (r) => {
          const zip5 = normalizeZip(r.zip);
          if (!zip5) return null;

          if (r.lat != null && r.lng != null) {
            return { ...r, zip: zip5 };
          }

          const z = await zipToLatLng(zip5);
          return z ? { ...r, zip: zip5, lat: z.lat, lng: z.lng } : null;
        })
      );

      if (!cancel) {
        setCustomers(enriched.filter(Boolean));
        setCustomersLoading(false);
      }
    } catch {
      if (!cancel) { setCustomers([]); setCustomersLoading(false); }
    }
  })();

  return () => { cancel = true; };
}, [profileUser?.u_id]);
// 2C — fetch nearby users when "Find Teammates" is active
useEffect(() => {
  const focus = (profileUser?.u_customer_focus || '').toUpperCase();
  if (activeLayer !== 'candidates' || !userCenter || !focus) return;

  let cancel = false;

  (async () => {
    try {
      setNearbyLoading(true);

      // Adjust the route name if yours differs:
      const url = `/api/profile/teams/local-users?focus=${encodeURIComponent(focus)}&lat=${userCenter.lat}&lng=${userCenter.lng}&radius=100`;

      const resp = await fetch(url, { credentials: 'include' });
      const data = resp.ok ? await resp.json() : [];

      if (!cancel) {
        const arr = Array.isArray(data) ? data : [];
        // Seed the same rich objects into the global store so TeamModal (global) can retrieve them later
        useOtherUsersStore.getState().seedFromArray(arr);
        setNearbyUsers(arr);
      }

    } catch (e) {
      if (!cancel) setNearbyUsers([]);
    } finally {
      if (!cancel) setNearbyLoading(false);
    }
  })();

  return () => { cancel = true; };
}, [activeLayer, userCenter, profileUser?.u_customer_focus]);

 /* 2A-GLOBAL — fetch GLOBAL candidates when Globe is ON */
 useEffect(() => {
   const focus = (profileUser?.u_customer_focus || '').toUpperCase();
   if (activeLayer !== 'candidates' || !userCenter || !focus || !isGlobalSearch) return;

   let cancel = false;

   (async () => {
     try {
       setGlobalLoading(true);

       // NEW endpoint (we will add this on the backend next step)
       const url = `/api/profile/teams/global-users?lat=${userCenter.lat}&lng=${userCenter.lng}`;

       const resp = await fetch(url, { credentials: 'include' });
       const data = resp.ok ? await resp.json() : [];

       if (!cancel) {
         const arr = Array.isArray(data) ? data : [];
         useOtherUsersStore.getState().seedFromArray(arr);
         setGlobalUsers(arr);
       }

     } catch (e) {
       if (!cancel) setGlobalUsers([]);
     } finally {
       if (!cancel) setGlobalLoading(false);
     }
   })();

   return () => { cancel = true; };
 }, [activeLayer, userCenter, profileUser?.u_customer_focus, isGlobalSearch]);

/* 2B — fetch nearby active teams when "Evaluate Teams" (local) is active */
useEffect(() => {
  if (activeLayer !== 'local' || !userCenter) return;

  let cancel = false;

  (async () => {
    try {
      setNearbyTeamsLoading(true);

      // Adjust the route name if yours differs. Expected shape:
      //   [{ team_id, team_name, lat, lng, b_address, b_city, b_state, b_zip_code, ... }, ...]
      // include my focus and my user id so the backend can filter deterministically
      const focus = (profileUser?.u_customer_focus || '').toUpperCase();
      const excludeOwnerId = Number(profileUser?.u_id) || '';
      const url = `/api/teams/recruiting/local?lat=${userCenter.lat}&lng=${userCenter.lng}&radius=${serviceDistance}&focus=${encodeURIComponent(focus)}&excludeOwnerId=${encodeURIComponent(excludeOwnerId)}`;



      const resp = await fetch(url, { credentials: 'include' });
      const data = resp.ok ? await resp.json() : [];

      if (!cancel) {
        const arr = Array.isArray(data) ? data : [];
        setNearbyTeams(arr);
        setNearbyTeamsLocal(arr, { center: userCenter, radius: 100 });
      }

    } catch (e) {
      if (!cancel) setNearbyTeams([]);
    } finally {
      if (!cancel) setNearbyTeamsLoading(false);
    }
  })();

  return () => { cancel = true; };
}, [activeLayer, userCenter]);

// 2B.1 — When teams list updates, compute a single lat/lng per team (b_lat/b_lng → lat/lng → ZIP centroid)
useEffect(() => {
  let cancelled = false;
  (async () => {
    if (!Array.isArray(nearbyTeams) || nearbyTeams.length === 0) {
      if (!cancelled) setTeamGeoOverride(new Map());
      return;
    }

    const entries = await Promise.all(
      nearbyTeams.map(async (t) => {
        const id = Number(t?.team_id || t?.id);
        if (!id) return null;

        // Prefer precise business coords (from team owner business)
        if (t?.b_lat != null && t?.b_lng != null) {
          return [id, { lat: +t.b_lat, lng: +t.b_lng, _src: 'db' }];
        }

        // Fallback: server-provided lat/lng
        if (t?.lat != null && t?.lng != null) {
          return [id, { lat: +t.lat, lng: +t.lng, _src: 'srv' }];
        }

        // ZIP centroid fallback (fast local dictionary)
        const zip5 = (t?.b_zip_code || t?.zip || '').toString().replace(/\D/g, '').slice(0, 5);
        if (zip5) {
          const z = await zipToLatLng(zip5);
          if (z) return [id, { lat: z.lat, lng: z.lng, _src: 'zip' }];
        }

        return null;
      })
    );

    if (!cancelled) {
      const map = new Map();
      entries.filter(Boolean).forEach(([id, pt]) => map.set(id, pt));
      setTeamGeoOverride(map);
      setTeamGeoOverrideLocal(map);

      // Fit bounds only on first mount (or when we explicitly reset hasFitRef elsewhere)
      if (mapRef.current && !hasFitRef.current && (map.size > 0 || userCenter)) {
        const g = window.google?.maps;
        if (g?.LatLngBounds) {
          const b = new g.LatLngBounds();
          if (userCenter) b.extend(userCenter);
          map.forEach((pt) => b.extend(pt));
          mapRef.current.fitBounds(b, { top: 24, right: 24, bottom: 24, left: 24 });
          hasFitRef.current = true; // ← prevents re-zoom on layer switches
        }
      }

    }
  })();

  return () => { cancelled = true; };
}, [nearbyTeams, userCenter, gReady]);



// When nearbyUsers update, geocode each teammate by ADDRESS first.
// Also log exactly what source we used for each user, so we can verify.
useEffect(() => {
  let cancelled = false;
  (async () => {
    if (!Array.isArray(nearbyUsers) || nearbyUsers.length === 0) return;

    const canGeocode = false;

    const entries = await Promise.all(
      nearbyUsers.map(async (u) => {
        const id = u?.u_id || u?.id;
        if (!id) return null;

        const first = [u?.u_first_name, u?.u_last_name].filter(Boolean).join(' ').trim();
        const biz   = (u?.b_name || '').toString().trim();

        // build address from the fields your API returns (b.* aliases)
        const zip5   = (u?.b_zip_code || u?.zip || '').toString().replace(/\D/g, '').slice(0, 5);
        const street = (u?.b_address || u?.address || '').toString().trim();
        const city   = (u?.b_city || u?.city || '').toString().trim();
        const state  = (u?.b_state || u?.state || '').toString().trim();

        const addrStr = [street, city, state, zip5].filter(Boolean).join(', ');

        // 1) Full street address (preferred)
        if (canGeocode && street && (city || state || zip5)) {
          const pt = await geocodeAddress({ street, city, state, zip: zip5 });
          if (pt) {
            console.log(`[cand] ${addrStr}  →  ADDR  (${pt.lat.toFixed(5)},${pt.lng.toFixed(5)})  #${id}  ${first}  ${biz}`);
            return [id, { lat: pt.lat, lng: pt.lng, _src: 'addr' }];
          } else {
            console.log(`[cand] ${addrStr}  →  ADDR-FAIL`);
          }
        } else {
          console.log(`[cand] (no complete street address)  ${first}  ${biz}  zip=${zip5 || 'n/a'}`);
        }

        // 2) Precise DB business coords (if present)
        if (u?.b_lat != null && u?.b_lng != null) {
          const pt = { lat: +u.b_lat, lng: +u.b_lng };
          console.log(`[cand] DB      →  DB   (${pt.lat.toFixed(5)},${pt.lng.toFixed(5)})  #${id}  ${first}  ${biz}`);
          return [id, { ...pt, _src: 'db' }];
        }

        // 3) Server coords (often ZIP centroid)
        if (u?.lat != null && u?.lng != null) {
          const pt = { lat: +u.lat, lng: +u.lng };
          console.log(`[cand] SERVER  →  SRV  (${pt.lat.toFixed(5)},${pt.lng.toFixed(5)})  #${id}  ${first}  ${biz}`);
          return [id, { ...pt, _src: 'srv' }];
        }

        // 4) ZIP centroid fallback
        if (zip5) {
          const z = await zipToLatLng(zip5);
          if (z) {
            console.log(`[cand] ZIP ${zip5} →  ZIP  (${z.lat.toFixed(5)},${z.lng.toFixed(5)})  #${id}  ${first}  ${biz}`);
            return [id, { lat: z.lat, lng: z.lng, _src: 'zip' }];
          }
        }

        console.log(`[cand] NO-COORDS  #${id}  ${first}  ${biz}`);
        return null;
      })
    );

    if (!cancelled) {
      const map = new Map();
      entries.filter(Boolean).forEach(([id, pt]) => map.set(id, pt));
      setCandidateGeoOverride(map);
    }
  })();

  return () => { cancelled = true; };
}, [nearbyUsers, gReady]);








  // Map options
// Map options
const mapOptions = {
  gestureHandling: 'greedy',
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: true,
  clickableIcons: true,

  // keep the map “north-up”, no rotation/tilt
  tilt: 0,
  heading: 0,
  rotateControl: false,
  mapTypeId: 'roadmap',
};

  // Hide the default "X" close button on Google InfoWindows (we close on hover-out)
  useEffect(() => {
    const id = 'gm-hide-close';
    if (!document.getElementById(id)) {
      const style = document.createElement('style');
      style.id = id;
      style.textContent = `.gm-ui-hover-effect{display:none!important;}`;
      document.head.appendChild(style);
    }
  }, []);

  const circleOptions = {
    strokeOpacity: 0.6, strokeColor: '#00aef7', strokeWeight: 2,
    fillOpacity: 0.08, fillColor: '#00aef7',
  };




useEffect(() => {
  const uid = profileUser?.u_id;
  if (!uid) return;

  (async () => {
    // ---- Customer count (exclude "Imported Reviews") ----
    try {
      const res = await fetch(`/api/customer-cards?user_id=${uid}`, { credentials: 'include' });
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      const real = list.filter(c => (c.customer_firstname || '').toLowerCase() !== 'imported');
      setTotalCustomers(real.length);
    } catch (err) {
      console.error('Failed to load customers:', err);
      setTotalCustomers(0);
    }

    // ---- Rating summary (avg + total reviews) ----
    try {
      const resp = await axios.get(`/api/profile/${uid}/rating-summary`, { withCredentials: true });
      setRatingData({
        u_avg_rating: parseFloat(resp.data.u_avg_rating),
        u_review_count: resp.data.total_reviews || 0,
      });
    } catch (err) {
      console.error('Failed to load ratings:', err);
      setRatingData({ u_avg_rating: null, u_review_count: 0 });
    }
  })();
}, [profileUser?.u_id]);

  // Add this useEffect to trigger suggestions when areas load and panel is open
  useEffect(() => {
    if (
      draftPanelOpen &&
      !loadingAreas &&
      areaNames.length > 0 &&
      proposedName &&
      proposedName !== 'Loading area...' &&
      !lockAvailability // ← do NOT refresh availability suggestions once user picked
    ) {
      onNameChange(proposedName);
    }
  }, [draftPanelOpen, loadingAreas, areaNames, proposedName, lockAvailability]);


  if (!profileUser) return <div></div>;



  return (
    <div className="container mx-auto w-4/5 pt-6 flex flex-col min-h-screen">
      {/* Lean-onboarding Slice 4 — blocking modal when focus + service-radius
          fields are missing. The mode='blocker' variant hides X/Cancel and
          locks the page; on save, needsFocusGate flips to false and the
          modal auto-unmounts. We mount the modal SIBLING to the page (not
          inside the grid) so its backdrop covers the full viewport. */}
      <FocusAndLogisticsModal
        mode="blocker"
        isOpen={needsFocusGate}
        onSaved={() => { /* state will re-render; nothing else needed here */ }}
      />
      <div className="grid grid-cols-1 md:grid-cols-[24fr_48fr_28fr] gap-3 flex-grow">


        {/* Left Column WRAPPER – contains Column 1, Column 1A (and later 1B) */}
        <div className="min-w-0 box-border flex flex-col gap-3">

        {/* === Column 1: User Identity Card === */}
        <div className="bg-white rounded-lg p-4 shadow-[0_0_20px_rgba(0,0,150,0.1)] h-fit">
          <div className="space-y-4">

            {/* === PROFILE CARD: Photo + Name + Title === */}
            <div
              className="rounded-lg p-4 bg-white transition-all duration-200"
              style={{
                boxShadow: '0 4px 12px -2px rgba(0, 174, 247, 0.25), 0 2px 4px -1px rgba(0, 174, 247, 0.15), inset 0 -8px 12px -8px rgba(0, 174, 247, 0.1)',
                border: '1px solid rgba(0, 174, 247, 0.2)',
                borderBottomColor: 'rgba(0, 174, 247, 0.35)',
                borderBottomWidth: '2px'
              }}
            >
              {/* User Profile Picture */}
              <div className="relative w-28 h-28 mx-auto mb-3">
                <div className="w-28 h-28 rounded-full border-2 border-[#00aef7]/30 overflow-hidden">
                  <img
                    src={
                      profileUser?.u_profile_picture
                        ? `${process.env.REACT_APP_API_URL}${profileUser.u_profile_picture}`
                        : '/images/default-avatar.png'
                    }
                    className="w-full h-full object-cover"
                    alt="Profile"
                  />
                </div>

                {/* Customer Focus Badge - matches column 2 styling */}
                {profileUser?.u_customer_focus && (
                  <div className="absolute -bottom-2 -right-6">
                    <div className="relative group">
                      <div className="text-sm font-bold bg-[#00aef7]/10 px-2 py-0.5 rounded-full border border-[#00aef7]/20 text-[#00aef7] cursor-help">
                        {displayUserFocus}
                      </div>
                      <div className="hidden group-hover:block absolute z-10 bg-black text-white text-xs p-2 rounded-md w-80 -translate-x-1/2 left-1/2 mt-6">
                        {userTooltip}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Name & Title */}
              <h2 className="text-lg font-bold text-gray-800 text-center">
                {profileUser?.u_first_name} {profileUser?.u_last_name}
              </h2>

            </div>



          </div>
        </div>


          {/* === Column 1A: My Teams === */}
          <div className="bg-white rounded-lg p-0 shadow-[0_0_20px_rgba(0,0,150,0.1)]">



          {/* === Column 1B: Your Team (draft → activate) === */}
          <div className="bg-white rounded-lg p-2 shadow-[0_0_20px_rgba(0,0,150,0.1)] overflow-hidden">
            <div className="flex items-center justify-center gap-3 mb-2">
              {/* Build another Team icon - only show when there's an active team */}
              {teamsLoaded && (draftTeam?.is_active || (Array.isArray(myTeams) && myTeams.some(t => t?.is_active))) && (
              <button
                type="button"
                onClick={() => setRegisterOpen(true)}
                className="w-6 h-6 flex items-center justify-center hover:opacity-80 transition"
                title="Build another Team"
                aria-label="Build another Team"
              >
                <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#00aef7"><path d="M739-83.5q-7-2.5-13-8.5L522-296q-6-6-8.5-13t-2.5-15q0-8 2.5-15t8.5-13l85-85q6-6 13-8.5t15-2.5q8 0 15 2.5t13 8.5l204 204q6 6 8.5 13t2.5 15q0 8-2.5 15t-8.5 13l-85 85q-6 6-13 8.5T754-81q-8 0-15-2.5Zm15-92.5 29-29-147-147-29 29 147 147ZM189.5-83q-7.5-3-13.5-9l-84-84q-6-6-9-13.5T80-205q0-8 3-15t9-13l212-212h85l34-34-165-165h-57L80-765l113-113 121 121v57l165 165 116-116-43-43 56-56H495l-28-28 142-142 28 28v113l56-56 142 142q17 17 26 38.5t9 45.5q0 24-9 46t-26 39l-85-85-56 56-42-42-207 207v84L233-92q-6 6-13 9t-15 3q-8 0-15.5-3Zm15.5-93 170-170v-29h-29L176-205l29 29Zm0 0-29-29 15 14 14 15Zm549 0 29-29-29 29Z"/></svg>
              </button>


              )}
              <h3 className="text-base font-semibold text-[#00aef7] text-center">My Teams</h3>
              {/* Recruiting Shortlist icon - only show when there's an active team */}
              {teamsLoaded && (draftTeam?.is_active || (Array.isArray(myTeams) && myTeams.some(t => t?.is_active))) && (
              <button
                type="button"
                onClick={handleRecruitClick}
                className="w-7 h-7 flex items-center justify-center hover:opacity-80 transition"
                title="Recruiting Shortlist"
                aria-label="Recruiting Shortlist"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height="24px"
                  width="24px"
                  viewBox="0 -960 960 960"
                  fill="currentColor"
                  aria-hidden="true"
                  className="w-6 h-6"
                  style={{
                    color: "#00aef7",
                    transform: "scale(1.12) translateY(1px)",
                    transformOrigin: "center",
                  }}
                >
                  <path d="M480-240Zm-320 80v-112q0-34 17.5-62.5T224-378q62-31 126-46.5T480-440q37 0 73 4.5t72 14.5l-67 68q-20-3-39-5t-39-2q-56 0-111 13.5T260-306q-9 5-14.5 14t-5.5 20v32h240v80H160Zm400 40v-123l221-220q9-9 20-13t22-4q12 0 23 4.5t20 13.5l37 37q8 9 12.5 20t4.5 22q0 11-4 22.5T903-340L683-120H560Zm300-263-37-37 37 37ZM620-180h38l121-122-18-19-19-18-122 121v38Zm141-141-19-18 37 37-18-19ZM367-527q-47-47-47-113t47-113q47-47 113-47t113 47q47 47 47 113t-47 113q-47 47-113 47t-113-47Zm169.5-56.5Q560-607 560-640t-23.5-56.5Q513-720 480-720t-56.5 23.5Q400-673 400-640t23.5 56.5Q447-560 480-560t56.5-23.5ZM480-640Z" />
                </svg>
              </button>


              )}
            </div>
          {/* Row: empty state (no teams) — shows + New team */}
          {teamsLoaded && !draftTeam && (!myTeams || myTeams.length === 0) && (
            <div className="p-0">
              <div className="flex flex-col items-center justify-center">
                <button
                  type="button"
                  onClick={() => setRegisterOpen(true)}
                  className="bg-[#00aef7] text-white px-4 py-2 rounded-md hover:opacity-90 transition"
                  title="Build your own Team"
                  aria-label="Build your own Team"
                >
                  Build your own Team
                </button>

              </div>
            </div>
          )}

          {/* === SCROLLABLE CONTAINER for all teams (draft + others) === */}
          <div style={{ maxHeight: '175px', overflowY: 'auto', paddingBottom: '100px' }} className="pr-1 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">

          {/* Row: team button — with hover micro-dock for edit/delete (ACTIVE: no micro-dock) */}
          {!(teamsLoaded && !draftTeam && (!myTeams || myTeams.length === 0)) && (
            <div className="group relative">
              {/* CENTERED: remove left padding/justify-start; force center */}
              <div className="flex items-center justify-center">
                {(draftTeam?.is_active &&
                  !(draftTeam?.team_name &&
                    (/^draft\b/i.test(String(draftTeam.team_name)) ||
                    /^team\s/i.test(String(draftTeam.team_name))))) ? (
                  /* =========================
                  * ACTIVE TEAM (button only) — centered + compact
                  * ========================= */
                  <button
                    type="button"
                    onClick={() => openTeam(draftTeam.team_id)}
                    onDragOver={onDragOverTeam(draftTeam.team_id)}
                    onDragEnter={onDragEnterTeam(draftTeam.team_id)}
                    onDragLeave={onDragLeaveTeam}
                    onDrop={onDropOnTeam(draftTeam.team_id)}
                    className={[
                      "mx-auto inline-flex items-center justify-center gap-2 rounded-md px-3 py-1.5 text-[11px] font-medium transition",
                      "text-[#00aef7] bg-white hover:bg-[#00aef7]/5",
                      Number(dragTargetTeamId) === Number(draftTeam.team_id) ? "ring-2 ring-[#00aef7]" : ""
                    ].join(" ")}
                    title="Drop a candidate here to add to this team's Recruiting Shortlist"
                  >
                    {teamButtonLabel}
                  </button>




                ) : (
                  /* =========================
                  * DRAFT (Activate + tiny delete dock) — centered + compact
                  * ========================= */
                  <>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={openActivate}
                        className="mx-auto inline-flex items-center gap-2 rounded-md border border-[#00aef7]/40 px-3 py-1.5 text-[11px] font-medium text-[#00aef7] hover:bg-[#00aef7]/5"
                        title="Open team activation"
                      >
                        {teamButtonLabel}
                      </button>
                      <span className="text-xs text-gray-500 select-none">Activate</span>
                    </div>

                    {/* micro-dock (draft): vertical tiny dock, floats at right */}
                    {!(renaming || confirmingDelete) && (
                      <div className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-150 flex flex-col gap-1">
                        <button
                          type="button"
                          onClick={() => {
                            setRenaming(false);
                            setConfirmingDelete(true);
                          }}
                          className="pointer-events-auto p-1 rounded-md border border-gray-300 text-gray-900 hover:bg-gray-100"
                          title="Delete draft team"
                          aria-label="Delete draft team"
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M6 7h12v14H6z" opacity=".2"/>
                            <path d="M9 3h6l1 2h4v2H4V5h4l1-2zm0 6h2v10H9V9zm4 0h2v10h-2V9z"/>
                          </svg>
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* ======================================================
              * Inline rename panel (horizontal: input + action buttons)
              * ====================================================== */}
              {renaming && (
                <div className="mt-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={renameValue}
                      onChange={(e) => { setRenameValue(e.target.value); if (renameError) setRenameError(''); }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const current = String(draftTeam?.team_name || '').trim();
                          const name = (renameValue || '').trim();
                          const unchanged = name === current;
                          if (name && !unchanged && !renameSaving) saveRename();
                        }
                        if (e.key === 'Escape') {
                          e.preventDefault();
                          setRenaming(false);
                          setRenameValue('');
                        }
                      }}
                      className="flex-1 rounded-md border border-[#00aef7] pl-1.5 pr-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#00aef7]/30"
                      placeholder="Edit team name"
                      autoFocus
                    />

                    {/* Save (green check) */}
                    {(() => {
                      const current = String(draftTeam?.team_name || '').trim();
                      const name = (renameValue || '').trim();
                      const disabled = !name || name === current || renameSaving;

                      return (
                        <button
                          type="button"
                          onClick={!disabled ? saveRename : undefined}
                          className={`p-1 rounded ${disabled ? 'opacity-40 pointer-events-none' : 'text-green-600 hover:text-green-700 hover:bg-green-100'}`}
                          title="Save"
                          aria-label="Save"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5"
                            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        </button>
                      );
                    })()}

                    {/* Cancel (red X) */}
                    <button
                      type="button"
                      onClick={() => { setRenaming(false); setRenameValue(''); }}
                      className="p-1 text-red-500 hover:text-red-700 hover:bg-red-100 rounded"
                      title="Cancel"
                      aria-label="Cancel"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5"
                        fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {renameError && (
                    <div className="mt-1 text-xs text-red-600">{renameError}</div>
                  )}
                </div>
              )}

              {/* ======================================================
              * Inline delete-confirm panel (horizontal confirm buttons)
              * ====================================================== */}
              {confirmingDelete && (
                <div className="mt-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-600">Delete this team?</span>

                    {/* Cancel */}
                    <button
                      type="button"
                      onClick={() => setConfirmingDelete(false)}
                      className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-700 hover:bg-gray-100"
                      title="Cancel delete"
                    >
                      Cancel
                    </button>

                    {/* Confirm */}
                    <button
                      type="button"
                      onClick={performDelete}
                      className="px-2 py-1 text-xs rounded border border-red-300 text-red-600 hover:bg-red-50"
                      title="Confirm delete"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}


          {/* ← CLOSES the guard: {!(teamsLoaded && !draftTeam && Array.isArray(myTeams) && myTeams.length === 0) && ( */}

          {Array.isArray(myTeams) && myTeams.length > 0 && (
  <ul className="mt-2 space-y-2 flex flex-col items-center">
    {myTeams
      // exclude the team currently shown in the activation row (if any)
      .filter(t => !draftTeam || Number(t?.team_id) !== Number(draftTeam?.team_id))
      .map((t) => {
        const teamId = Number(t?.team_id);

        return (
          <li key={teamId} className="relative w-full">
            <div className="w-full flex items-center justify-center">
              <button
                type="button"
                onClick={() => openTeam(t.team_id)}
                onDragOver={onDragOverTeam(t.team_id)}
                onDragEnter={onDragEnterTeam(t.team_id)}
                onDragLeave={onDragLeaveTeam}
                onDrop={onDropOnTeam(t.team_id)}
                className={[
                  "mx-auto inline-flex items-center justify-center gap-2 rounded-md px-3 py-1.5 text-[11px] font-medium transition focus:outline-none",
                  "text-[#00aef7] bg-white hover:bg-[#00aef7]/5",
                  Number(dragTargetTeamId) === Number(t.team_id) ? "ring-2 ring-[#00aef7]" : ""
                ].join(" ")}
                title="Drop a candidate here to add to this team's Recruiting Shortlist"
              >
                {t.team_name || `Team ${t.team_id}`}
              </button>
            </div>
          </li>
        );
      })}
  </ul>
          )}

          </div>{/* ← CLOSES the scrollable container for all teams */}





        {/* (removed duplicate empty-state; the real one is rendered above the button row) */}

          {/* Inline activation panel (appears after clicking Activate) */}
          {draftPanelOpen && (
            <div className="mt-3 rounded-md border border-gray-200 p-3">
              {/* Instructions area - ALWAYS show this, loading spinner goes below it */}
              <div className="mb-3 text-sm text-gray-700">
              {(() => {
                // While areas are loading, keep neutral instructions (no warning)
                if (loadingAreas) {
                  return (
                    <>
                      Choose a clear, local area name (e.g., <em>examples loading…</em>).<br />
                      You can name your team anything you want, but team names must be unique.
                    </>
                  );
                }

                const raw = Array.isArray(areaNames) ? areaNames : [];
                const cleaned = [];
                const seen = new Set();
                for (const s of raw) {
                  if (!s) continue;
                  const base = String(s)
                    .replace(/\b(central|east|west|north|south|heights)\b/gi, '')
                    .replace(/\s{2,}/g, ' ')
                    .trim();
                  if (!base) continue;
                  const key = base.toLowerCase();
                  if (!seen.has(key)) {
                    seen.add(key);
                    cleaned.push(base);
                  }
                  if (cleaned.length >= 5) break;
                }

                if (cleaned.length >= 3) {
                  return (
                    <>
                      Choose a clear, local area name (e.g., <em>{cleaned.slice(0, 5).join(', ')}</em>).<br />
                      Names must be unique.
                    </>
                  );
                }

                if (cleaned.length > 0) {
                  return (
                    <>
                      Choose a clear, local area name (e.g., <em>{cleaned.join(', ')}</em>).<br />
                      Names must be unique.
                    </>
                  );
                }

                // If loading is finished and we still have none, show a simple fallback (no amber warning)
                return (
                  <>
                    Choose a unique team name for your local area.
                  </>
                );
              })()}

              </div>

              {/* Loading spinner - goes BELOW the instructions */}
              {loadingAreas && (
                <div className="flex items-center gap-2 mb-3">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#00aef7]"></div>
                  <span className="text-sm text-gray-500">Loading local area suggestions...</span>
                </div>
              )}

              <label className="block text-sm text-gray-700 mb-1">Team name</label>
                <input
                  type="text"
                  value={proposedName}
                  placeholder="e.g., Milk District"
                  onChange={(e) => { setLockAvailability(false); onNameChange(e.target.value); }} // ← typing re-enables availability
                  className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#00aef7]/30"
                />
                {/* Availability (name check) */}
                <div className="mt-2 text-xs">
                  {checking && <span className="text-gray-500">Checking availability…</span>}
                  {!checking && available === true && (
                    <span className="text-green-600">Available ✓</span>
                  )}
                  {!checking && available === false && (
                    <span className="text-red-600">That name is already taken.</span>
                  )}
                </div>

                {/* Suggestions from availability checker */}
                {!lockAvailability && suggestions.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {suggestions.map((s, idx) => {
                      // In post-pick mode, strip directions/Heights and never append a mascot
                      let base = String(s);
                      if (pickedOnce) {
                        base = base
                          .replace(/\b(Central|East|West|North|South|Heights)\b/gi, '')
                          .replace(/\s{2,}/g, ' ')
                          .trim();
                      }

                      let label;
                      if (pickedOnce) {
                        // After a user has picked a name once, do NOT add a mascot
                        label = base;
                      } else {
                        // Pre-pick behavior: append 1 mascot unless the suggestion already ends with one
                        const m = mascotAt(idx);
                        const last = base.trim().split(/\s+/).pop()?.toLowerCase();
                        const endsWithMascot =
                          Array.isArray(mascots) && mascots.some(ms => String(ms).toLowerCase() === last);
                        label = (!endsWithMascot && m) ? `${base} ${m}` : base;
                      }

                      return (
                        <button
                          key={`${label}-${idx}`}
                          type="button"
                          onClick={() => { setPickedOnce(true); setLockAvailability(true); onNameChange(label); }}
                          className="rounded-full border px-2 py-1 text-xs hover:bg-gray-50"
                          title="Use this suggestion"
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                )}





                {/* Local area suggestions (from areaNames) */}
                {!loadingAreas && Array.isArray(areaNames) && areaNames.length > 0 && (
                  <div className="mt-3">
                    <div className="text-xs text-gray-500 mb-1">Local area suggestions</div>
                    <div className="flex flex-wrap gap-2">
                      {areaNames.map((a, idx) => {
                        const base = String(a).split(',')[0]
                          .replace(/\b(Central|East|West|North|South|Heights)\b/gi, '')
                          .replace(/\s{2,}/g, ' ')
                          .trim();
                        const m = mascotAt(idx + 100);
                        const label = m ? `${base} ${m}` : base;
                        return (
                          <button
                            key={`${label}-${idx}`}
                            type="button"
                            onClick={() => { setPickedOnce(true); setLockAvailability(true); onNameChange(label); }}
                            className="rounded-full border px-2 py-1 text-xs hover:bg-gray-50"
                            title="Use this area"
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}




              <div className="mt-3 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setDraftPanelOpen(false)}
                  className="px-3 py-1.5 text-sm rounded-md border hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={activateNow}
                  disabled={!proposedName.trim() || activating}
                  className={`px-3 py-1.5 text-sm rounded-md ${
                    !proposedName.trim() || activating
                      ? 'bg-[#00aef7]/40 text-white cursor-not-allowed'
                      : 'bg-[#00aef7] text-white hover:bg-[#0099da]'
                  }`}
                >
                  {activating ? 'Activating…' : 'Activate'}
                </button>
              </div>
            </div>
          )}
          </div>
          </div>

        </div>



{/* Middle Column WRAPPER – contains 2A (top) and 2B (below) */}
<div className="min-w-0 box-border flex flex-col gap-2 mb-0">

{/* === Column 2A: Map Controls === */}

{/* Row 1 — Service Radius is always on; Customers/Candidates are radios */}
{/* === Column 2A: Map Controls === */}
<div className="min-w-0 box-border bg-white rounded-lg p-3 shadow-[0_0_20px_rgba(0,0,150,0.1)] h-[80px] mb-0">
  <h2 className="text-xl font-bold text-[#00aef7] mb-1 text-center">
    Service Area Map
  </h2>

{/* Row 1 — Service Area + radios (Find Teammates moved left; Find Teams right-aligned) */}
<div className="grid grid-cols-3 gap-2">
  {/* Service Area — always on */}
  <div className="flex items-center justify-center text-sm">
    <button
      type="button"
      onClick={openDistanceModal}
      className="inline-flex items-center gap-1 text-[#00aef7] font-small underline underline-offset-2 decoration-dotted hover:decoration-solid rounded px-2 py-0.5 hover:bg-[#00aef7]/5 focus:outline-none focus:ring-2 focus:ring-[#00aef7]/40"
      title={isOwner ? 'Edit service distance' : 'Only owners can edit'}
    >
      <span>{`Service Area ${serviceDistance} mi`}</span>
    </button>
  </div>

      {/* Find Teammates (moved left one spot) */}
      <label className="flex items-center justify-center gap-2 text-sm">
        <input
          type="radio"
          name="mapLayer"
          value="candidates"
          className="w-4 h-4 accent-[#00ea4d]"
          checked={activeLayer === 'candidates'}
          onChange={() => setActiveLayer('candidates')}
        />
        <span>Recruit Teammates</span>
      </label>

      {/* Find Teams (moved to row 1, right-aligned) */}
      <label className="flex items-center justify-center gap-2 text-sm">
        <input
          type="radio"
          name="mapLayer"
          value="local"
          className="w-4 h-4 accent-[#00aef7]"
          checked={activeLayer === 'local'}
          onChange={() => setActiveLayer('local')}
        />
        <span>Join Teams</span>
      </label>
    </div>
</div>



  {/* === Column 2B: Teams Section (existing content) === */}
  <div className="min-w-0 box-border bg-white rounded-lg p-4 shadow-[0_0_20px_rgba(0,0,150,0.1)] h-[430px] overflow-hidden pr-2 relative">
  {/* === Map === */}
  <div className="h-full rounded-md overflow-hidden">
    {userCenter ? (
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%' }}
        center={userCenter}
        zoom={8.5}
        options={mapOptions}
        onLoad={(map) => {
          mapRef.current = map;
          setGReady(true);
          // If the circle is already loaded, fit to it immediately
          if (circleRef.current && !hasFitRef.current) {
            const bounds = circleRef.current.getBounds?.();
            if (bounds) {
              map.fitBounds(bounds, { top: 8, right: 8, bottom: 8, left: 8 });
              hasFitRef.current = true;
            }
          }
        }}
      >
        {/* Red business pin — open tooltip on hover; close when rolling off both pin and card */}
        <Marker
          position={userCenter}
          icon={redPinIcon()}
          options={{ shape: redPinShape, optimized: false, zIndex: 1000 }} // ← add optimized:false
          onMouseOver={() => {
            if (closeTimer.current) { clearTimeout(closeTimer.current); }
            setShowBizTip(true);
          }}
          
          onMouseOut={() => {
            closeTimer.current = setTimeout(() => {
              if (!overInfo) setShowBizTip(false);
            }, 600);
          }}
        />


        {showBizTip && (
          <InfoWindow
            position={userCenter}
  options={
    window.google?.maps
      ? { disableAutoPan: true, pixelOffset: new window.google.maps.Size(0, -36) }
      : undefined
  }
            onCloseClick={() => setShowBizTip(false)}
          >

            <div
              className="text-sm leading-tight"
              onMouseEnter={() => {
                if (closeTimer.current) { clearTimeout(closeTimer.current); }
                setOverInfo(true);
              }}
              onMouseLeave={() => {
                setOverInfo(false);
                setShowBizTip(false);
              }}
            >
              <div className="font-semibold">{displayName}</div>
              {businessName && <div className="text-gray-800">{businessName}</div>}

              {(addrLine1 || addrLine2) && (
                <div className="mt-1 text-gray-600">
                  {addrLine1 && <div>{addrLine1}</div>}
                  {addrLine2 && <div>{addrLine2}</div>}
                </div>
              )}
            </div>
          </InfoWindow>
        )}


        {/* Circle: onLoad captures the bounds and fits once */}

<Circle
  key={`circle-${serviceDistance}-${userCenter?.lat}-${userCenter?.lng}`}  // force React remount on change
  center={userCenter}
  radius={serviceMeters}
  options={circleOptions}
  onLoad={(circle) => {
    // clear any prior overlay so we never keep two rings
    if (circleRef.current && circleRef.current !== circle) {
      try { circleRef.current.setMap(null); } catch {}
    }
    circleRef.current = circle;

    if (mapRef.current && !hasFitRef.current) {
      const bounds = circle.getBounds?.();
      if (bounds) {
        mapRef.current.fitBounds(bounds, { top: 16, right: 16, bottom: 16, left: 16 });
        hasFitRef.current = true;
      }
    }
  }}
  onUnmount={() => {
    if (circleRef.current) {
      try { circleRef.current.setMap(null); } catch {}
      circleRef.current = null;
    }
  }}
/>



{activeLayer === 'customers' && Array.isArray(customers) && customers.length > 0 &&
  customers.map((c) => {
    const pinSize = 26;
    const pos = { lat: Number(c?.lat), lng: Number(c?.lng) };
    if (!pos.lat || !pos.lng) return null;

    return (
      <Marker
        key={c.id || `${pos.lat},${pos.lng}`}
        position={pos}
        options={{ optimized: false }} // match green pins (no flicker)
        icon={{
          url: YELLOW_PIN, // same sprite family/shape as green/blue
          scaledSize: new window.google.maps.Size(pinSize, pinSize),
          anchor: new window.google.maps.Point(pinSize / 2, pinSize),
        }}
        title={markerTitleForCustomer(c, userCenter)}
      />
    );
  })
}




{/* Teammate candidates (green pins, yellow on hover) */}
  {activeLayer === 'candidates' &&
    Array.isArray(nearbyUsers) &&
    nearbyUsers.map((u) => {
        const pinSize = 26;
        const id  = u.u_id || u.id;
        const ovr = candidateGeoOverride.get(id);
        let pos = ovr
          ? { lat: ovr.lat, lng: ovr.lng }
          : (u?.b_lat != null && u?.b_lng != null) ? { lat: +u.b_lat, lng: +u.b_lng }
          : (u?.lat != null && u?.lng != null) ? { lat: +u.lat, lng: +u.lng }
          : null;

        if (!pos) return null;




      const isHL = hoverUserId != null && hoverUserId === id;



      return (
        <Marker
          key={id || `${u.lat},${u.lng}`}
          position={pos}
          zIndex={isHL ? 999 : undefined}
          title={`src:${ovr?._src || (u?.b_lat!=null?'db':(u?.lat!=null?'srv':'zip'))}`}
          icon={{
            url: isHL ? YELLOW_PIN : GREEN_PIN,
            scaledSize: new window.google.maps.Size(pinSize, pinSize),
            anchor: new window.google.maps.Point(pinSize / 2, pinSize),
          }}
          onMouseOver={() => {
            if (candidateCloseTimer.current) clearTimeout(candidateCloseTimer.current);
            setCandidateTip({ user: u, pos });
          }}
          onMouseOut={() => {
            candidateCloseTimer.current = setTimeout(() => {
              if (!overCandidateInfo.current) setCandidateTip(null);
            }, 400);
          }}
          onClick={() => {
            // Tell the list to open the modal with this candidate
            window.dispatchEvent(new CustomEvent('candidate:openModal', { detail: u }));
          }}
        />
      );
    })
  }

{activeLayer === 'candidates' && candidateTip && (
  <InfoWindow
    position={candidateTip.pos}
    options={
      window.google?.maps
        ? { disableAutoPan: true, pixelOffset: new window.google.maps.Size(0, -36) }
        : undefined
    }
  >
    <div
      className="text-sm leading-tight"
      onMouseEnter={() => {
        if (candidateCloseTimer.current) clearTimeout(candidateCloseTimer.current);
        overCandidateInfo.current = true;
      }}
      onMouseLeave={() => {
        overCandidateInfo.current = false;
        setCandidateTip(null);
      }}
    >
      <div className="font-semibold">
        {(candidateTip.user?.u_first_name || '') + ' ' + (candidateTip.user?.u_last_name || '')}
      </div>
      {candidateTip.user?.b_name && (
        <div className="text-gray-800">{candidateTip.user.b_name}</div>
      )}
      <div className="text-gray-600">{candidateTip.user?.b_industry || ''}</div>
    </div>
  </InfoWindow>
)}






        {/* Team candidates (blue) */}
        {showCandidates && overlays.size === 0 &&
          candidates.map((m) => (
            <Marker
              key={m.id || `${m.lat},${m.lng}`}
              position={{ lat: +m.lat, lng: +m.lng }}
              icon={BLUE_PIN}
            />
          ))}
        {/* === Evaluate Teams (BLUE) — IDENTICAL STRUCTURE TO GREEN === */}
        {isLocal && userCenter && (
          <>
            {nearbyTeams.map((t) => {
              const pinSize = 26;
              const id  = Number(t.team_id ?? t.id);
              const ovr = teamGeoOverride.get(id);

              // base pos is still used for “src:” title tracing
              const basePos = ovr
                ? { lat: ovr.lat, lng: ovr.lng }
                : (t?.b_lat != null && t?.b_lng != null) ? { lat: +t.b_lat, lng: +t.b_lng }
                : (t?.lat != null && t?.lng != null) ? { lat: +t.lat, lng: +t.lng }
                : null;

              if (!basePos) return null;

              // ✅ use micro-offset (if stacked), else fall back to basePos
              const pos = teamPinPosById.get(id) || basePos;


              const isHL = hoverTeamId != null && hoverTeamId === id;

              return (
                <Marker
                  key={id || `${pos.lat},${pos.lng}`}
                  position={pos}
                  zIndex={isHL ? 999 : undefined}
                  title={`src:${ovr?._src || (t?.b_lat!=null?'db':(t?.lat!=null?'srv':'zip'))}`}
                  icon={{
                    url: isHL ? YELLOW_PIN : BLUE_PIN,
                    scaledSize: new window.google.maps.Size(pinSize, pinSize),
                    anchor: new window.google.maps.Point(pinSize / 2, pinSize),
                  }}
                  onMouseOver={() => {
                    if (teamCloseTimer.current) clearTimeout(teamCloseTimer.current);
                    setTeamTip({ team: t, pos });
                  }}
                  onMouseOut={() => {
                    teamCloseTimer.current = setTimeout(() => {
                      if (!overTeamInfo.current) setTeamTip(null);
                    }, 400);
                  }}
                  onClick={() => {
                    // (kept parallel to green; safe no-op if nothing listens)
                    window.dispatchEvent(new CustomEvent('team:openModal', { detail: t }));
                  }}
                />
              );
            })}

            {teamTip && (
              <InfoWindow
                position={teamTip.pos}
                options={
                  window.google?.maps
                    ? { disableAutoPan: true, pixelOffset: new window.google.maps.Size(0, -36) }
                    : undefined
                }
              >
                <div
                  className="text-sm leading-tight"
                  onMouseEnter={() => {
                    if (teamCloseTimer.current) clearTimeout(teamCloseTimer.current);
                    overTeamInfo.current = true;
                  }}
                  onMouseLeave={() => {
                    overTeamInfo.current = false;
                    setTeamTip(null);
                  }}
                >
                  <div className="font-semibold text-[#00aef7]">
                    {teamTip.team?.team_name ?? 'Team'}
                  </div>
                  <div className="text-gray-600">
                    {(teamTip.team?.b_city || '')}
                    {teamTip.team?.b_city && teamTip.team?.b_state ? ', ' : ''}
                    {teamTip.team?.b_state || ''}
                  </div>
                </div>
              </InfoWindow>
            )}
          </>
        )}





      </GoogleMap>
    ) : (
      <div className="flex items-center justify-center h-full text-sm text-gray-500">
        Loading map…
      </div>
    )}
  </div>


  </div>
  {showDistanceModal && (
  <div className="fixed inset-0 z-50 flex items-center justify-center">
    <div
      className="absolute inset-0"
      onClick={() => setShowDistanceModal(false)}
      aria-hidden="true"
    />
    <div className="relative z-10 w-[340px] max-w-[90vw] rounded-xl bg-white shadow-xl border border-gray-100 p-4">
      <h4 className="text-base font-semibold text-[#00aef7] mb-2">Service Area</h4>

      <div className="text-sm text-gray-700 mb-2">
        Current value: <span className="font-semibold">{serviceDistance} miles</span>
      </div>

      <label className="block text-sm text-gray-600 mb-1">New distance (miles)</label>
      <input
        type="number"
        min={1}
        max={500}
        step={1}
        value={pendingDistance}
        onChange={(e) => setPendingDistance(e.target.value)}
        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#00aef7]/40"
        disabled={!isOwner}
      />

      {!isOwner && (
        <p className="mt-2 text-xs text-gray-500">
          Only the <span className="font-medium">owner</span> can change this setting.
        </p>
      )}

      <div className="mt-4 flex items-center justify-end gap-2">
        <button
          type="button"
          className="px-3 py-1.5 text-sm rounded-md border border-gray-300 hover:bg-gray-50"
          onClick={() => setShowDistanceModal(false)}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSaveDistance}
          disabled={!isOwner || savingDistance}
          className={`px-3 py-1.5 text-sm rounded-md ${
            !isOwner || savingDistance
              ? 'bg-[#00aef7]/40 text-white cursor-not-allowed'
              : 'bg-[#00aef7] text-white hover:bg-[#0099da]'
          }`}
        >
          {savingDistance ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  </div>
)}

</div>

{activeLayer === 'candidates' ? (
  <LocalUsersTeamList
    users={isGlobalSearch ? globalUsers : nearbyUsers}
    loading={isGlobalSearch ? globalLoading : nearbyLoading}
    onHoverUser={(id) => setHoverUserId(id)}
    isGlobalSearch={isGlobalSearch}
    onToggleGlobalSearch={() => setIsGlobalSearch((v) => !v)}
  />
) : activeLayer === 'local' ? (
  <LocalTeamsList onHoverTeam={(id) => setHoverTeamId(id)} />
) : (
  <MyCustomerTeamList
    customers={customers}
    loading={customersLoading}
    userCenter={userCenter}
    distanceMiles={distanceMiles}
  />
)}











      </div>

      {/* Footer */}
      <footer className="mt-6">{/* Optional footer content */}</footer>


      {/* Mini team-pick modal (shown when the viewer has 2+ teams) */}
      {teamPickOpen && (
        <div className="fixed inset-0 z-[120]">
          <div className="absolute inset-0" />
          <div className="absolute left-1/2 top-20 w-[360px] max-w-[92vw] -translate-x-1/2 rounded-xl bg-white shadow-2xl border p-4">
            <div className="text-base font-semibold mb-2">Which team are you recruiting for?</div>
            <div className="text-xs text-gray-600 mb-3">
              Select a team and click <span className="font-medium">Continue</span>.
            </div>

            <div className="space-y-2 mb-4 max-h-[40vh] overflow-auto">
              {teamPickList.map((t) => {
                const tid = Number(t.team_id);
                return (
                  <label key={tid} className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="team-pick"
                      className="h-4 w-4"
                      checked={Number(teamPickSelected) === tid}
                      onChange={() => setTeamPickSelected(tid)}
                    />
                    <span className="truncate">
                      {String(t.team_name || 'Team').trim()}{" "}
                      <span className="text-gray-500 text-xs">({t.role})</span>
                    </span>
                  </label>
                );
              })}
            </div>

            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setTeamPickOpen(false)}
                className="px-3 py-1.5 text-sm rounded-md border hover:bg-gray-50"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={async () => {
                  if (!Number.isFinite(Number(teamPickSelected))) return;
                  const tid = Number(teamPickSelected);
                  setTeamId(tid);
                  await loadTeamList(tid);
                  setTeamPickOpen(false);
                  openRecruiting();
                }}
                disabled={!Number.isFinite(Number(teamPickSelected))}
                className={`px-3 py-1.5 text-sm rounded-md ${
                  !Number.isFinite(Number(teamPickSelected))
                    ? 'bg-[#00aef7]/40 text-white cursor-not-allowed'
                    : 'bg-[#00aef7] text-white hover:bg-[#0099da]'
                }`}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      <RegisterTeamModal
        open={registerOpen}
        onClose={() => setRegisterOpen(false)}
        title="Register Your Team"
      >
        {/* Instructions (moved from inline panel) */}
        <div className="mb-3 text-sm text-gray-700">
          {(() => {
            const raw = Array.isArray(areaNames) ? areaNames : [];
            const cleaned = [];
            const seen = new Set();
            for (const s of raw) {
              if (!s) continue;
              const base = String(s)
                .replace(/\b(central|east|west|north|south|heights)\b/gi, '')
                .replace(/\s{2,}/g, ' ')
                .trim();
              if (!base) continue;
              const key = base.toLowerCase();
              if (!seen.has(key)) {
                seen.add(key);
                cleaned.push(base);
              }
              if (cleaned.length >= 5) break;
            }

            if (cleaned.length >= 3) {
              return (
                <>
                  Choose a clear, local area name (e.g., <em>{cleaned.slice(0, 5).join(', ')}</em>).<br />
                  Names must be unique.
                </>
              );
            }

            if (cleaned.length > 0) {
              return (
                <>
                  Choose a clear, local area name (e.g., <em>{cleaned.join(', ')}</em>).<br />
                  Names must be unique.
                </>
              );
            }

            return <>Choose a unique team name for your local area.</>;
          })()}
        </div>

        {/* Loading indicator under the instructions (same placement as inline) */}
        {loadingAreas && (
          <div className="flex items-center gap-2 mb-3">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#00aef7]" />
            <span className="text-sm text-gray-500">Loading local area suggestions…</span>
          </div>
        )}

        {/* Team Name + availability */}
        <label className="block text-sm font-medium text-gray-700 mb-1">Team name</label>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={proposedName}
            onChange={(e) => onNameChange(e.target.value)}
            className="flex-1 rounded-md border border-[#00aef7]/50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#00aef7]/30"
            placeholder="e.g., Hunters Creek"
            autoFocus
          />
          {/* Availability badge */}
          {checking ? (
            <span className="text-xs text-gray-500">Checking…</span>
          ) : available === true ? (
            <span className="text-xs text-green-600">Available ✓</span>
          ) : available === false ? (
            <span className="text-xs text-red-600">Taken</span>
          ) : null}
        </div>

        {/* Suggestions from availability checker (EXACT behavior from inline) */}
        {!lockAvailability && suggestions.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {suggestions.map((s, idx) => {
              // Post-pick: strip directions/Heights and never append a mascot
              let base = String(s);
              if (pickedOnce) {
                base = base
                  .replace(/\b(Central|East|West|North|South|Heights)\b/gi, '')
                  .replace(/\s{2,}/g, ' ')
                  .trim();
              }

              let label;
              if (pickedOnce) {
                label = base;
              } else {
                // Pre-pick: append 1 mascot unless suggestion already ends with one
                const m = mascotAt(idx);
                const last = base.trim().split(/\s+/).pop()?.toLowerCase();
                const endsWithMascot =
                  Array.isArray(mascots) && mascots.some(ms => String(ms).toLowerCase() === last);
                label = (!endsWithMascot && m) ? `${base} ${m}` : base;
              }

              return (
                <button
                  key={`${label}-${idx}`}
                  type="button"
                  onClick={() => { setPickedOnce(true); setLockAvailability(true); onNameChange(label); }}
                  className="rounded-full border px-2 py-1 text-xs hover:bg-gray-50"
                  title="Use this suggestion"
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}

        {/* Local area suggestions (exact behavior from inline; mascot applied) */}
        {!loadingAreas && Array.isArray(areaNames) && areaNames.length > 0 && (
          <div className="mt-3">
            <div className="text-xs text-gray-500 mb-1">Local area suggestions</div>
            <div className="flex flex-wrap gap-2">
              {areaNames.map((a, idx) => {
                const base = String(a).split(',')[0]
                  .replace(/\b(Central|East|West|North|South|Heights)\b/gi, '')
                  .replace(/\s{2,}/g, ' ')
                  .trim();
                const m = mascotAt(idx + 100);
                const label = m ? `${base} ${m}` : base;
                return (
                  <button
                    key={`${label}-${idx}`}
                    type="button"
                    onClick={() => { setPickedOnce(true); setLockAvailability(true); onNameChange(label); }}
                    className="rounded-full border px-2 py-1 text-xs hover:bg-gray-50"
                    title="Use this area"
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => setRegisterOpen(false)}
            className="px-3 py-1.5 text-sm rounded-md border hover:bg-gray-50"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={activateNow}
            disabled={!proposedName.trim() || checking || available === false || activating}
            className={`px-3 py-1.5 text-sm rounded-md ${
              !proposedName.trim() || checking || available === false || activating
                ? 'bg-[#00aef7]/40 text-white cursor-not-allowed'
                : 'bg-[#00aef7] text-white hover:bg-[#0099da]'
            }`}
          >
            {activating ? 'Activating…' : 'Activate'}
          </button>
        </div>
      </RegisterTeamModal>

{inviteTeamPickOpen && (
  <div className="fixed inset-0 z-[12000] flex items-center justify-center">
    {/* Backdrop */}
    <div
      className="absolute inset-0 bg-black/30"
      onClick={() => setInviteTeamPickOpen(false)}
    />

    {/* Modal */}
    <div className="relative w-[520px] max-w-[92vw] bg-white rounded-2xl shadow-2xl overflow-hidden">
      <div className="px-4 py-2 bg-[#00aef7] text-white flex items-center justify-between">
        <div className="font-semibold text-sm">Choose a Team</div>
        <button
          type="button"
          onClick={() => setInviteTeamPickOpen(false)}
          className="inline-flex items-center justify-center rounded-md px-2 py-1 text-white hover:bg-white/15"
          aria-label="Close"
          title="Close"
        >
          ✕
        </button>
      </div>

      <div className="p-4">
        <div className="text-sm text-gray-700 mb-3">
          Select exactly one team to invite someone to.
        </div>

        <div className="max-h-[340px] overflow-auto pr-1 space-y-2">
          {(Array.isArray(myTeams) ? myTeams : []).map((t) => {
            const id = Number(t?.team_id);
            const name = String(t?.team_name || `Team ${t?.team_id || ''}`).trim();
            const selected = Number(inviteTeamPickSelected) === id;

            return (
              <button
                key={id}
                type="button"
                onClick={() => setInviteTeamPickSelected(id)}
                className={[
                  "w-full text-left px-3 py-2 rounded-md border transition",
                  selected
                    ? "bg-[#00aef7]/10 border-[#00aef7]/40"
                    : "bg-white border-gray-200 hover:bg-gray-50",
                ].join(" ")}
              >
                <div className="text-sm font-semibold text-gray-900">{name}</div>
              </button>
            );
          })}
        </div>

        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => setInviteTeamPickOpen(false)}
            className="px-3 py-1.5 rounded-md text-sm font-semibold bg-white text-gray-500 border border-gray-200 hover:bg-gray-50"
          >
            Cancel
          </button>

          <button
            type="button"
            disabled={!Number.isFinite(Number(inviteTeamPickSelected))}
            onClick={() => {
              const teamId = Number(inviteTeamPickSelected);
              if (!Number.isFinite(teamId)) return;

              // TEMP scaffold: simulate an incoming invite (TeamModal reads this)
              localStorage.setItem(
                'eh_team_invite_banner',
                JSON.stringify({
                  mode: 'incoming',
                  invitee_u_id: profileUser?.u_id || null,
                  invitee_name: profileUser?.u_first_name || 'User',
                })
              );

              setInviteTeamPickOpen(false);
              openTeam(teamId);
            }}
            className={[
              "px-3 py-1.5 rounded-md text-sm font-semibold",
              Number.isFinite(Number(inviteTeamPickSelected))
                ? "bg-[#00aef7] text-white hover:bg-[#0097d6]"
                : "bg-gray-200 text-gray-500 cursor-not-allowed",
            ].join(" ")}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  </div>
)}

    </div>
  );
}


export default TeamInfo;
